import React, { useState, useEffect, useRef } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';
import { Container, Row, Col, Card, Table, Button, Spinner, Alert, Badge, Form, Modal, Dropdown, ProgressBar } from 'react-bootstrap';
import { FaCamera, FaBoxOpen, FaClipboardList, FaVideo, FaImage, FaEdit, FaTimes, FaExternalLinkAlt, FaPlus, FaCog, FaQuestionCircle, FaInfoCircle, FaFileExcel, FaFilePdf, FaTrash, FaMoon, FaSun, FaRedo, FaSearch, FaChartPie, FaEyeSlash, FaEye, FaRobot, FaCheckCircle, FaExclamationTriangle, FaShieldAlt, FaSort, FaSortUp, FaClock } from 'react-icons/fa';
import './App.css';
import logo from './logo.svg';
import { API_BASE, API_ROOT } from './config';
import { SecureImage, SmartThumbnail } from './components/SecureMedia';
import ItemDetailModal from './components/ItemDetailModal';
import AnalysisInbox from './components/AnalysisInbox';
import BulkEditBar from './components/BulkEditBar';
import ImportWizard from './components/ImportWizard';
import SettingsModal from './components/SettingsModal';


const TURNSTILE_SITEKEY = process.env.REACT_APP_TURNSTILE_SITEKEY || '0x4AAAAAAC8yNTQ5yY5XJopc';
window._pendingTurnstileToken = null;
window.onTurnstileSuccess = (token) => {
  window._pendingTurnstileToken = token;
  window.dispatchEvent(new CustomEvent('turnstile-verified', { detail: token }));
};

const translations = {
    en: {
      appTitle: "Expatory",
      subtitle: "AI-Powered Home Inventory Assistant",
      loginTitle: "Expatory",
      loginButton: "Login",
      registerButton: "Create Account",
      setupTitle: "Setup Admin",
      setupButton: "Create Admin Account",
      confirmPassword: "Confirm Password",
      passwordsDoNotMatch: "Passwords do not match",
      dashboard: "Inventory Dashboard",
      fullInventory: "Full Inventory",
      searchPlaceholder: "Search items...",
      editList: "Edit List",
      save: "Save",
      cancel: "Cancel",
      delete: "Delete",
      item: "Item",
      type: "Type",
      room: "Room",
      storage: "Storage",
      qty: "Qty",
      yearPurchased: "Year Purchased",
      cost: "Unit Price",
      cond: "Cond.",
      cert: "Cert.",
      settingsTitle: "Personal Settings",
      localization: "Localization Preferences",
      language: "Language",
      currency: "Currency Code",
      location: "Location / Country",
      roomList: "Room List",
      containerList: "Container List",
      tutorial: "Tutorial",
      about: "About",
      logout: "Logout",
      eraseData: "Erase All Data",
      totalValue: "Total Value",
      totalItems: "Total Items",
      topCategories: "Top Categories",
      justFound: "Just Found (Review)",
      confirmSave: "Confirm & Save",
      discardAll: "Discard All",
      photo: "Photo",
      video: "Video",
      gallery: "Gallery",
      manual: "Manual",
      export: "Export to Excel",
      exporting: "Exporting…",
      exportImageWarning: "Photos increase file size significantly. Above 300 items they are automatically excluded — filter by room to include them.",
      exportImagesDisabled: "Export complete (photos excluded — too many items). Filter by room to export with photos.",
      import: "Import Excel",
      template: "Download Template",
      saving: "Saving...",
      loading: "Loading...",
      loadMore: "Load More",
      noItems: "No items yet. Start scanning!",
      noMatches: "No items match your filters.",
      confirmDelete: "Are you sure you want to delete this item?",
      updateSuccess: "Update successful!",
      refreshSuccess: "AI successfully updated the price and replacement link!",
      estCost: "Est. Cost",
      adminPanel: "Admin Control Panel",
      refreshList: "Refresh List",
      userEmail: "User Email",
      created: "Created",
      apiUsage: "API Usage",
      actions: "Actions",
      aboutContent: "An AI-powered home inventory system built with FastAPI, React, and Google Gemini.",
      tutorialStep1Title: "1. Scan Items",
      tutorialStep1Content: "Use 'Photo' or 'Video' to capture your belongings. Videos are great for scanning whole rooms in one go!",
      tutorialStep2Title: "2. AI Identification",
      tutorialStep2Content: "Wait a moment while Gemini identifies your items, estimates costs, and assesses condition.",
      tutorialStep3Title: "3. Review Findings",
      tutorialStep3Content: "New items appear in the 'Just Found' section. You can edit names, quantities, or prices before saving.",
      tutorialStep4Title: "4. Bulk Import",
      tutorialStep4Content: "Already have a list? Use our template and upload via 'Import Excel' in Settings.",
      tutorialStep5Title: "5. Build your Inventory",
      tutorialStep5Content: "Once saved, items move to your permanent inventory. Click any row to see the source image or jump to the exact video timestamp.",
      accountSecurity: "Account Security",
      updateEmail: "Update Email",
      currentPassword: "Current Password",
      newPassword: "New Password",
      confirmNew: "Confirm New",
      update: "Update",
      newEmailAddress: "New Email Address",
      change: "Change",
      deleteAccount: "Delete Account",
      deleteAccountWarning: "This will permanently erase all items, media, and settings.",
      yourPassword: "Your Password",
      typeDeleteToConfirm: "Type 'DELETE' to confirm",
      eraseMyAccount: "ERASE MY ACCOUNT",
      passwordComplexityHint: "Min 8 chars, 1 uppercase, 1 number.",
      serverDiagnostics: "SERVER DIAGNOSTICS",
      reTest: "RE-TEST",
      fixImages: "FIX IMAGES",
      standard: "Standard",
      exhaustive: "Exhaustive",
      initializingSecureConnection: "Establishing secure connection...",
      urlNotConfigured: "URL NOT CONFIGURED",
      serverReachable: "Server reachable",
      connectionTimeout: "Connection timed out",
      appErrorApiNotConfigured: "APP ERROR: API URL not configured!",
      emailNotVerified: "Email not verified. Please check your inbox.",
      typeDeleteConfirmPrompt: "Please type 'DELETE' to confirm.",
      confirmDeleteEverything: "Are you sure? This will delete ALL of your data.",
      passwordMismatch: "Passwords do not match",
      accountDeleted: "Account deleted.",
      emailUpdatedReconnect: "Email updated! Please log in again.",
      evidenceGallery: "Evidence Gallery",
      attaching: "Attaching...",
      addMedia: "Add Photo/Video",
      mainBadge: "MAIN",
      inventoryDetails: "Inventory Details",
      foundAt: "Found at:",
      viewReplacement: "View Replacement",
      refreshAiData: "Refresh AI Data",
      findReplacementLink: "Find Replacement Link",
      rotateView: "Rotate View",
      fixBoxAlignment: "Fix Box Alignment",
      chooseDifferentImage: "Choose Different Image",
      imageVariantInstruction: "Click a variant to set it as the primary photo.",
      dangerZone: "Danger Zone",
      confirmDeleteAllTitle: "Are you absolutely sure you want to delete everything?",
      confirmDeleteAllBody: "This will permanently erase all inventory records and all uploaded photos and videos.",
      deleteAllConfirm: "Yes, Delete Everything",
      userControlPanel: "User Control Panel",
      adminUserDeleted: "User deleted.",
      adminDeleteUserConfirm: "Are you sure? This will delete ALL data for this user!",
      selectRoom: "Select Room...",
      selectStorage: "Select Storage...",
      selectCategory: "Select category...",
      allCategories: "All categories",
      addRoom: "Add Room...",
      addContainer: "Add Container...",
      chooseEmailAdmin: "Choose Email (Admin)",
      choosePassword: "Choose Password",
      emailAddress: "Email Address",
      password: "Password",
      passwordPlaceholder: "Min 8 chars, 1 number, 1 caps",
      alreadyHaveAccount: "Already have an account? Login",
      dontHaveAccount: "Don't have an account? Register here",
      close: "Close",
      allCategoriesLabel: "All Items",
      noDataYet: "No data yet",
      acrossAllCategories: "Across all categories",
      itemsInInventory: "items",
      confirmDeleteAttachment: "Delete permanently?",
      mediaAttached: "Media attached!",
      primaryPhotoUpdated: "Primary photo updated!",
      categoryPlaceholder: "Category...",
      itemsAddedSuccess: "Items added successfully!",
      mediaAttachedSuccess: "Media attached successfully!",
      itemNameRequired: "Item name is required.",
      invalidLogin: "Invalid email or password",
      registrationSuccess: "Account created! Please verify your email.",
      setupFailed: "Setup failed",
      adminCreatedSuccess: "Admin created! Please log in.",
      manualEntryTitle: "Manual Item Entry",
      addToInventory: "Add to Inventory",
      uploadingEvidence: "Uploading evidence...",
      initializingAiAnalysis: "Initializing AI Analysis...",
      videoAnalysisWarning: "This may take up to 30 seconds for videos.",
      scansProcessing: "scan(s) in progress",
      scanFailed: "Scan failed",
      retryUpload: "Retry Upload",
      retryAi: "Retry AI",
      noMediaAvailable: "No Media Available",
      uncategorized: "Uncategorized",
      unknownItem: "Unknown Item",
      searchTimeout: "The AI search is taking too long.",
      eraseSuccess: "Database and media cleared.",
      exportFailed: "Export failed: ",
      downloadFailed: "Download failed: ",
      adminDeleteFailed: "Delete failed",
      passwordUpdated: "Password updated!",
      settingsTabAccount: "Account",
      settingsTabRooms: "Rooms & Storage",
      settingsTabLocation: "Location & Currency",
      dataExport: "Data Export",
      andUploadIt: "and upload it",
      importExcel: "Import Excel",
      itemsImported: "items imported!",
      analysisTasks: "Active Analyses",
      noActiveTasks: "No active background tasks.",
      reviewReady: "Ready for Review",
      failedTask: "Analysis Failed",
      viewResults: "View Results",
      retryTask: "Retry Analysis",
      taskHistory: "Analysis History",
      analyzingInBackground: "Analyzing in background...",
      privacyData: "Privacy & Data",
      dataResidency: "Data Residency",
      serverLocation: "Server: Hetzner, Germany",
      encryptionStatus: "Status: AES-256 E2EE",
      aiPrivacy: "AI Privacy",
      aiPrivacyContent: "Paid tier Gemini API. Your data is NOT used for model training.",
      dataRetention: "Data Retention",
      dataRetentionContent: "Account deletion purges all data and media files.",
      userAcceptancePolicy: "User Acceptance Policy",
      uapSummary: "By using Expatory, you agree to this policy.",
      uapTitle: "Expatory User Acceptance Policy",
      uapSection1Title: "1. Data We Collect",
      uapSection1Content: "We collect email, hashed password, inventory details, and uploaded media.",
      uapSection2Title: "2. Data Protection",
      uapSection2Content: "Data is encrypted at rest using AES-256.",
      uapSection3Title: "3. No Selling",
      uapSection3Content: "We never sell or share your data with third parties.",
      uapSection4Title: "4. AI Processing",
      uapSection4Content: "Media is temporarily sent to Google for identification.",
      uapSection5Title: "5. Accounts",
      uapSection5Content: "You may delete your account at any time.",
      uapSection6Title: "6. Liability",
      uapSection6Content: "Expatory is provided 'as is'. You are responsible for backups.",
      uapSection7Title: "7. Availability",
      uapSection7Content: "Service may be interrupted for maintenance.",
      uapSection8Title: "8. AI Accuracy",
      uapSection8Content: "AI results are estimates and not professional appraisals.",
      uapSection9Title: "9. Age",
      uapSection9Content: "You must be 16+ to use this service.",
      uapSection10Title: "10. Updates",
      uapSection10Content: "We may update this policy. Check for changes.",
      uapSection11Title: "11. Law",
      uapSection11Content: "Governed by the laws of Canada.",
      waitForVerification: "Waiting for human verification...",
      verificationPending: "Human Verification Pending",
      verified: "Verified",
      searchGoogle: "Search Google",
      exportPdf: "Export Insurance PDF",
      includeImages: "Include Photos (Larger File)",
      filterByRoom: "Filter by Room",
      filterByType: "Filter by Category",
      allRooms: "All Rooms",
      minItemValue: "Min. Item Value",
      generateReport: "Generate Report",
      tooManyRequests: "Too many requests.",
      retryVerification: "Retry Verification",
      loggingIn: "Logging in...",
      registering: "Registering...",
      verificationLoading: "Verifying you're human...",
      advancedDiagnostics: "Advanced",
      forgotPassword: "Forgot password?",
      forgotPasswordTitle: "Reset your password",
      forgotPasswordDesc: "Enter your email and we'll send you a reset link.",
      sendResetLink: "Send Reset Link",
      resetEmailSent: "If that email is registered, a reset link is on its way. Check your inbox (and spam folder).",
      backToLogin: "Back to login",
      resetPasswordTitle: "Choose a new password",
      resetPasswordDesc: "Enter a new password for your account.",
      newPasswordLabel: "New password",
      confirmNewPassword: "Confirm new password",
      updatePassword: "Update Password",
      resetLinkExpired: "This reset link is invalid or has expired. Please request a new one.",
      resetSuccess: "Password updated! Redirecting to login...",
      filterItem: "Filter...",
      allStorage: "All Storage",
      allConditions: "All Conditions",
      condGood: "Good",
      condFair: "Fair",
      condPoor: "Poor",
      clearFilters: "Clear filters",
      recentlyAdded: "Recently Added",
      sortedBy: "Sorted by",
      showRows: "Show",
      showAll: "All",
      editingSubset: "Editing filtered subset",
      selectItems: "Select",
      selected: "selected",
      bulkApply: "Apply",
      bulkApplied: "{n} items updated",
      bulkDelete: "Delete Selected",
      bulkDeleteWarning: "This will permanently delete {n} items and all their media. This cannot be undone.",
      bulkDeleteConfirm: "Yes, delete {n} items",
      bulkDeleted: "{n} items deleted",
      showing: "Showing",
      of: "of",
      page: "Page",
      pagePrev: "Previous page",
      pageNext: "Next page",
      pageLoadMore: "Load more pages",
      onboardingTitle: "Welcome to Expatory!",
      onboardingSubtitle: "Here's how to build your inventory in 3 steps:",
      onboardingStep1Title: "📸 Scan a room or item",
      onboardingStep1Body: "Take a photo or short video — even a whole room at once.",
      onboardingStep2Title: "🤖 AI identifies everything",
      onboardingStep2Body: "Gemini names items, estimates replacement values, and assesses condition automatically.",
      onboardingStep3Title: "📋 Review & save",
      onboardingStep3Body: "Items appear in the \"Just Found\" inbox. Edit anything before confirming to your permanent inventory.",
      onboardingCta: "Start Scanning",
      onboardingSkip: "I'll explore on my own",
      onboardingArrow: "↑ Use the scan buttons above to add your first item",
      showOnboarding: "Getting Started",
      importWizardTitle: "Import from Excel",
      importStep1Title: "Upload your spreadsheet",
      importStep1Desc: "Drag & drop your file, or click to browse — .xlsx and .xls accepted.",
      importDropHere: "Drop your Excel file here",
      importOrBrowse: "or click to browse",
      importAnalyzing: "Reading file…",
      importStep2Title: "Match your columns",
      importStep2Desc: "Tell us which column in your file matches each Expatory field.",
      importColFrom: "Your column",
      importColSample: "Sample values",
      importColTo: "Maps to",
      importSkip: "— Skip —",
      importPreviewTitle: "Preview",
      importDoImport: "Import {n} items",
      importImporting: "Importing…",
      importNoItemCol: "Map at least one column to \"Item\" to continue.",
      importStep3Title: "Import complete!",
      importItemsAdded: "items added to your inventory.",
      importGoInventory: "View my inventory",
      replacementLink: "Replacement Link",
    },
    fr: {
      appTitle: "Expatory",
      subtitle: "Assistant d'inventaire IA",
      loginTitle: "Connexion",
      loginButton: "Se connecter",
      registerButton: "Créer un compte",
      setupTitle: "Configuration Admin",
      setupButton: "Créer Admin",
      confirmPassword: "Confirmer le mot de passe",
      passwordsDoNotMatch: "Mots de passe différents",
      dashboard: "Tableau de bord",
      fullInventory: "Inventaire complet",
      searchPlaceholder: "Rechercher...",
      editList: "Modifier",
      save: "Enregistrer",
      cancel: "Annuler",
      delete: "Supprimer",
      item: "Article",
      type: "Catégorie",
      room: "Pièce",
      storage: "Rangement",
      qty: "Qté",
      yearPurchased: "Année d'achat",
      cost: "Prix unit.",
      cond: "État",
      cert: "Cert.",
      settingsTitle: "Paramètres",
      localization: "Localisation",
      language: "Langue",
      currency: "Devise",
      location: "Pays",
      roomList: "Pièces",
      containerList: "Rangements",
      tutorial: "Tutoriel",
      about: "À propos",
      logout: "Déconnexion",
      eraseData: "Effacer tout",
      totalValue: "Valeur totale",
      totalItems: "Total articles",
      topCategories: "Catégories",
      justFound: "Vient de trouver",
      confirmSave: "Confirmer",
      discardAll: "Tout rejeter",
      photo: "Photo",
      video: "Vidéo",
      gallery: "Galerie",
      manual: "Manuel",
      export: "Exporter",
      exporting: "Exportation…",
      exportImageWarning: "Les photos augmentent la taille du fichier. Au-delà de 300 articles elles sont exclues automatiquement — filtrez par pièce pour les inclure.",
      exportImagesDisabled: "Export terminé (photos exclues — trop d'articles). Filtrez par pièce pour exporter avec photos.",
      import: "Importer",
      template: "Modèle",
      saving: "Enregistrement...",
      loading: "Chargement...",
      loadMore: "Plus",
      noItems: "Aucun article.",
      noMatches: "Aucun résultat.",
      confirmDelete: "Supprimer ?",
      updateSuccess: "Succès !",
      refreshSuccess: "Prix actualisé !",
      estCost: "Coût Est.",
      adminPanel: "Admin",
      refreshList: "Actualiser",
      userEmail: "Email",
      created: "Créé",
      apiUsage: "Utilisation",
      actions: "Actions",
      aboutContent: "Inventaire domestique par IA (React/FastAPI/Gemini).",
      tutorialStep1Title: "1. Scannez",
      tutorialStep1Content: "Prenez des photos ou vidéos.",
      tutorialStep2Title: "2. Analyse",
      tutorialStep2Content: "Gemini identifie les objets.",
      tutorialStep3Title: "3. Révisez",
      tutorialStep3Content: "Modifiez avant d'enregistrer.",
      tutorialStep4Title: "4. Importez",
      tutorialStep4Content: "Utilisez notre fichier Excel.",
      tutorialStep5Title: "5. Gérez",
      tutorialStep5Content: "Votre inventaire est prêt.",
      accountSecurity: "Sécurité",
      updateEmail: "Email",
      currentPassword: "Mot de passe actuel",
      newPassword: "Nouveau mot de passe",
      confirmNew: "Confirmer",
      update: "Mettre à jour",
      newEmailAddress: "Nouvel email",
      change: "Changer",
      deleteAccount: "Supprimer le compte",
      deleteAccountWarning: "Action irréversible.",
      yourPassword: "Mot de passe",
      typeDeleteToConfirm: "Tapez 'DELETE'",
      eraseMyAccount: "EFFACER",
      passwordComplexityHint: "Min 8 chars, 1 MAJ, 1 chiffre.",
      serverDiagnostics: "DIAGNOSTICS",
      reTest: "TESTER",
      fixImages: "IMAGES",
      standard: "Standard",
      exhaustive: "Exhaustif",
      initializingSecureConnection: "Connexion sécurisée...",
      urlNotConfigured: "URL NON CONFIGURÉE",
      serverReachable: "Connecté",
      connectionTimeout: "Délai dépassé",
      appErrorApiNotConfigured: "ERREUR API",
      emailNotVerified: "Email non vérifié.",
      typeDeleteConfirmPrompt: "Tapez 'DELETE'.",
      confirmDeleteEverything: "Effacer TOUTES vos données ?",
      passwordMismatch: "Mots de passe différents",
      accountDeleted: "Compte supprimé.",
      emailUpdatedReconnect: "Email mis à jour !",
      evidenceGallery: "Galerie",
      attaching: "Ajout...",
      addMedia: "Ajouter",
      mainBadge: "PRINCIPAL",
      inventoryDetails: "Détails",
      foundAt: "Trouvé :",
      viewReplacement: "Remplacement",
      refreshAiData: "Actualiser IA",
      findReplacementLink: "Trouver lien",
      rotateView: "Pivoter",
      fixBoxAlignment: "Ajuster",
      chooseDifferentImage: "Changer l'image",
      imageVariantInstruction: "Cliquez pour définir.",
      dangerZone: "Zone de danger",
      confirmDeleteAllTitle: "Tout effacer ?",
      confirmDeleteAllBody: "Action irréversible.",
      deleteAllConfirm: "Oui, tout effacer",
      userControlPanel: "Utilisateurs",
      adminUserDeleted: "Supprimé.",
      adminDeleteUserConfirm: "Supprimer cet utilisateur ?",
      selectRoom: "Pièce...",
      selectStorage: "Rangement...",
      selectCategory: "Catégorie...",
      allCategories: "Toutes les catégories",
      addRoom: "Ajouter",
      addContainer: "Ajouter",
      chooseEmailAdmin: "Email (Admin)",
      choosePassword: "Mot de passe",
      emailAddress: "Email",
      password: "Mot de passe",
      passwordPlaceholder: "Min 8 chars",
      alreadyHaveAccount: "Déjà un compte ?",
      dontHaveAccount: "Pas de compte ?",
      close: "Fermer",
      allCategoriesLabel: "Tous",
      noDataYet: "Rien",
      acrossAllCategories: "Tout",
      itemsInInventory: "articles",
      searchTimeout: "La recherche IA prend trop de temps.",
      eraseSuccess: "Base de données et médias effacés avec succès.",
      exportFailed: "Échec de l'exportation : ",
      downloadFailed: "Échec du téléchargement : ",
      adminDeleteFailed: "Échec de la suppression",
      passwordUpdated: "Mot de passe mis à jour !",
      settingsTabAccount: "Compte",
      settingsTabRooms: "Pièces et rangements",
      settingsTabLocation: "Localisation et devise",
      dataExport: "Exportation des données",
      andUploadIt: "et téléchargez",
      importExcel: "Importer",
      itemsImported: "importés !",
      mediaAttached: "Média ajouté !",
      primaryPhotoUpdated: "Mis à jour !",
      categoryPlaceholder: "Catégorie...",
      itemsAddedSuccess: "Ajoutés !",
      mediaAttachedSuccess: "Attaché !",
      itemNameRequired: "Nom requis.",
      invalidLogin: "Email/Pass incorrect",
      registrationSuccess: "Compte créé ! Vérifiez vos mails.",
      setupFailed: "Échec",
      adminCreatedSuccess: "Admin créé !",
      manualEntryTitle: "Saisie Manuelle",
      addToInventory: "Ajouter",
      uploadingEvidence: "Envoi...",
      initializingAiAnalysis: "Analyse IA...",
      videoAnalysisWarning: "Max 30s.",
      scansProcessing: "analyse(s) en cours",
      scanFailed: "Échec",
      retryUpload: "Réessayer",
      retryAi: "Réessayer IA",
      noMediaAvailable: "Rien",
      uncategorized: "Non classé",
      unknownItem: "Inconnu",
      confirmDeleteAttachment: "Supprimer ?",
      analysisTasks: "Analyses",
      noActiveTasks: "Rien.",
      reviewReady: "Révision",
      failedTask: "Échec",
      viewResults: "Voir",
      retryTask: "Réessayer",
      taskHistory: "Historique",
      analyzingInBackground: "Analyse...",
      privacyData: "Données",
      dataResidency: "Résidence",
      serverLocation: "Hetzner, DE",
      encryptionStatus: "AES-256 E2EE",
      aiPrivacy: "IA",
      aiPrivacyContent: "Gemini API payant.",
      dataRetention: "Rétention",
      dataRetentionContent: "Tout est purgé.",
      userAcceptancePolicy: "Acceptation",
      uapSummary: "En créant un compte ou en utilisant Expatory, vous reconnaissez avoir lu, compris et accepté les termes de cette politique.",
      uapTitle: "Politique d'acceptation",
      uapSection1Title: "1. Données",
      uapSection1Content: "Email, pass, inventaire.",
      uapSection2Title: "2. Protection",
      uapSection2Content: "Chiffré AES-256.",
      uapSection3Title: "3. Tiers",
      uapSection3Content: "Aucune vente.",
      uapSection4Title: "4. IA",
      uapSection4Content: "Transit vers Google.",
      uapSection5Title: "5. Comptes",
      uapSection5Content: "Libre suppression.",
      uapSection6Title: "6. Responsabilité",
      uapSection6Content: "Tel quel.",
      uapSection7Title: "7. Service",
      uapSection7Content: "Maintenance possible.",
      uapSection8Title: "8. IA",
      uapSection8Content: "Estimations seulement.",
      uapSection9Title: "9. Âge",
      uapSection9Content: "16+ ans.",
      uapSection10Title: "10. Mises à jour",
      uapSection10Content: "Révision possible.",
      uapSection11Title: "11. Loi",
      uapSection11Content: "Lois du Canada.",
      waitForVerification: "Vérification...",
      verificationPending: "En attente",
      verified: "Vérifié",
      searchGoogle: "Google",
      exportPdf: "Exporter PDF",
      includeImages: "Photos",
      filterByRoom: "Par pièce",
      filterByType: "Par catégorie",
      allRooms: "Toutes",
      minItemValue: "Valeur min.",
      generateReport: "Générer",
      tooManyRequests: "Trop de requêtes.",
      retryVerification: "Réessayer la vérification",
      loggingIn: "Connexion...",
      registering: "Inscription...",
      verificationLoading: "Vérification en cours...",
      advancedDiagnostics: "Avancé",
      forgotPassword: "Mot de passe oublié\u00a0?",
      forgotPasswordTitle: "Réinitialiser votre mot de passe",
      forgotPasswordDesc: "Entrez votre adresse courriel et nous vous enverrons un lien.",
      sendResetLink: "Envoyer le lien",
      resetEmailSent: "Si cette adresse est enregistrée, un lien est en route. Vérifiez votre boîte de réception (et vos courriels indésirables).",
      backToLogin: "Retour à la connexion",
      resetPasswordTitle: "Choisissez un nouveau mot de passe",
      resetPasswordDesc: "Entrez un nouveau mot de passe pour votre compte.",
      newPasswordLabel: "Nouveau mot de passe",
      confirmNewPassword: "Confirmer le nouveau mot de passe",
      updatePassword: "Mettre à jour le mot de passe",
      resetLinkExpired: "Ce lien est invalide ou a expiré. Veuillez en demander un nouveau.",
      resetSuccess: "Mot de passe mis à jour\u00a0! Redirection vers la connexion...",
      filterItem: "Filtrer...",
      allStorage: "Tous rangements",
      allConditions: "Tous états",
      condGood: "Bon",
      condFair: "Passable",
      condPoor: "Mauvais",
      clearFilters: "Effacer filtres",
      recentlyAdded: "Ajout récent",
      sortedBy: "Trié par",
      showRows: "Afficher",
      showAll: "Tout",
      editingSubset: "Sous-ensemble filtré",
      selectItems: "Sélectionner",
      selected: "sélectionné(s)",
      bulkApply: "Appliquer",
      bulkApplied: "{n} articles mis à jour",
      bulkDelete: "Supprimer la sélection",
      bulkDeleteWarning: "Ceci supprimera définitivement {n} articles et tous leurs médias. Cette action est irréversible.",
      bulkDeleteConfirm: "Oui, supprimer {n} articles",
      bulkDeleted: "{n} articles supprimés",
      showing: "Affichage",
      of: "sur",
      page: "Page",
      pagePrev: "Page précédente",
      pageNext: "Page suivante",
      pageLoadMore: "Charger plus de pages",
      onboardingTitle: "Bienvenue sur Expatory !",
      onboardingSubtitle: "Créez votre inventaire en 3 étapes :",
      onboardingStep1Title: "📸 Scannez une pièce ou un objet",
      onboardingStep1Body: "Prenez une photo ou une courte vidéo — même une pièce entière d'un coup.",
      onboardingStep2Title: "🤖 L'IA identifie tout",
      onboardingStep2Body: "Gemini nomme les articles, estime leur valeur de remplacement et évalue leur état.",
      onboardingStep3Title: "📋 Révisez et enregistrez",
      onboardingStep3Body: "Les articles apparaissent dans la boîte « Vient de trouver ». Modifiez avant de confirmer.",
      onboardingCta: "Commencer le scan",
      onboardingSkip: "Explorer par moi-même",
      onboardingArrow: "↑ Utilisez les boutons ci-dessus pour ajouter votre premier article",
      showOnboarding: "Premiers pas",
      importWizardTitle: "Importer depuis Excel",
      importStep1Title: "Déposez votre fichier",
      importStep1Desc: "Glissez-déposez votre fichier ou cliquez pour parcourir — .xlsx et .xls acceptés.",
      importDropHere: "Déposez votre fichier Excel ici",
      importOrBrowse: "ou cliquez pour parcourir",
      importAnalyzing: "Lecture du fichier…",
      importStep2Title: "Associez vos colonnes",
      importStep2Desc: "Indiquez à quelle donnée Expatory correspond chaque colonne de votre fichier.",
      importColFrom: "Votre colonne",
      importColSample: "Exemples",
      importColTo: "Correspond à",
      importSkip: "— Ignorer —",
      importPreviewTitle: "Aperçu",
      importDoImport: "Importer {n} articles",
      importImporting: "Importation…",
      importNoItemCol: "Associez au moins une colonne au champ « Article » pour continuer.",
      importStep3Title: "Importation terminée !",
      importItemsAdded: "articles ajoutés à votre inventaire.",
      importGoInventory: "Voir mon inventaire",
      replacementLink: "Lien de remplacement",
    }
};

function App() {
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [theme, setTheme] = useState(localStorage.getItem('theme') || 'light');
  const [language, setLanguage] = useState(localStorage.getItem('language') || 'en');
  const [registering, setRegistering] = useState(false);
  const [authMode, setAuthMode] = useState(null); // null | 'forgot' | 'reset'
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotSubmitted, setForgotSubmitted] = useState(false);
  const [resetToken, setResetToken] = useState('');
  const [resetNewPassword, setResetNewPassword] = useState('');
  const [resetConfirmPassword, setResetConfirmPassword] = useState('');
  const [resetError, setResetError] = useState('');
  const [resetSuccess, setResetSuccess] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState(null);
  const [turnstileStatus, setTurnstileStatus] = useState('waiting'); // 'waiting', 'verified', 'error', 'timeout'
  const [authLoading, setAuthLoading] = useState(true);
  const [setupRequired, setSetupRequired] = useState(false);

  const t = translations[language];

  useEffect(() => {
    // If auth is still loading, the login form isn't in the DOM yet — skip entirely.
    // The effect will re-run once authLoading flips to false and the form mounts.
    if (authLoading) return;

    // Reset status on each fresh appearance of the login form unless already verified.
    if (!window._pendingTurnstileToken) {
      setTurnstileStatus('waiting');
    }

    // Use window._pendingTurnstileToken (not the stale closure value of turnstileToken)
    // so the timeout check is accurate even if the token arrived after render.
    let timeoutTimer = setTimeout(() => {
      if (!window._pendingTurnstileToken) setTurnstileStatus('timeout');
    }, 15000);

    const handleVerified = (e) => {
      setTurnstileToken(e.detail);
      setTurnstileStatus('verified');
      clearTimeout(timeoutTimer);
    };
    window.addEventListener('turnstile-verified', handleVerified);

    const renderWidget = () => {
      const container = document.getElementById('turnstile-widget');
      if (window.turnstile && container && container.children.length === 0) {
        try {
          window.turnstile.render('#turnstile-widget', {
            sitekey: TURNSTILE_SITEKEY,
            callback: window.onTurnstileSuccess,
            theme: theme
          });
        } catch (e) { console.warn("Turnstile Render Error:", e); }
      }
    };

    // Always register the listener first, then fire immediately if the API is already loaded
    window.addEventListener('turnstile-ready', renderWidget);
    if (window._turnstileReady) {
      renderWidget();
    }

    // Catch any token that arrived before the listener was added
    if (window._pendingTurnstileToken) {
      setTurnstileToken(window._pendingTurnstileToken);
      setTurnstileStatus('verified');
      clearTimeout(timeoutTimer);
    }

    return () => {
      window.removeEventListener('turnstile-verified', handleVerified);
      window.removeEventListener('turnstile-ready', renderWidget);
      clearTimeout(timeoutTimer);
    };
  }, [registering, theme, authLoading, token]);

  // Read ?reset_token= from URL on mount and switch to reset mode
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const tok = params.get('reset_token');
    if (tok) {
      setResetToken(tok);
      setAuthMode('reset');
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  const [analysisTasks, setAnalysisTasks] = useState([]);
  const [userRole, setUserRole] = useState(localStorage.getItem('userRole'));
  const [authLoading2, setAuthLoading2] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState({ status: 'checking', details: '' });
  const [inventory, setInventory] = useState([]);
  const [stats, setStats] = useState({ total_value: 0, item_count: 0, category_distribution: {} });
  const [statsError, setStatsError] = useState(false);
  const [viewMode, setViewMode] = useState('table');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [isEditing, setIsEditing] = useState(false);
  const [showStats, setShowStats] = useState(true);
  const [adminUsers, setAdminUsers] = useState([]);
  const [adminLoading, setAdminLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [status, setStatus] = useState('');
  const [newItems, setNewItems] = useState([]);
  const [activeTaskId, setActiveTaskId] = useState(null);
  const [error, setError] = useState(null);
  const [lastUploadedFile, setLastUploadedFile] = useState(null);
  const [uploadFailed, setUploadFailed] = useState(false);
  const [aiFailed, setAiFailed] = useState(false);
  const [bgScans, setBgScans] = useState([]);
  const [showPdfModal, setShowPdfModal] = useState(false);
  const [pdfOptions, setPdfOptions] = useState({ includeImages: false, rooms: [], type: '', min_value: 0 });
  const [showExcelModal, setShowExcelModal] = useState(false);
  const [excelOptions, setExcelOptions] = useState({ room: '', includeImages: false });
  const [excelExporting, setExcelExporting] = useState(false);
  const [pdfExporting, setPdfExporting] = useState(false);
  const [editedInventory, setEditedInventory] = useState([]);
  const [dirtyItemIds, setDirtyItemIds] = useState(new Set());
  const [scanPrecision, setScanPrecision] = useState('standard'); 
  const CATEGORIES = [
    "Electronics", "Appliances", "Furniture", "Kitchenware", "Bedding & Linens",
    "Clothing & Accessories", "Jewellery & Watches", "Art & Decor", "Tools & Hardware",
    "Sports & Recreation", "Books & Leisure", "Media, Toys & Games",
    "Musical Instruments", "Health & Personal Care", "Miscellaneous", "Holiday & Costumes",
  ];
  const [roomList, setRoomList] = useState([]);
  const [containerList, setContainerList] = useState([]);
  const [currency, setCurrency] = useState('CAD');
  const [location, setLocation] = useState('Canada');
  const [showDetail, setShowDetail] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [showTutorial, setShowTutorial] = useState(false);
  const [showAbout, setShowAbout] = useState(false);
  const [showEraseConfirm, setShowEraseConfirm] = useState(false);
  const [showManualModal, setShowManualModal] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [showUAPModal, setShowUAPModal] = useState(false);
  const [passwordForm, setPasswordForm] = useState({ current: '', next: '', confirm: '' });
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [emailForm, setEmailForm] = useState({ password: '', next: '' });
  const [deleteForm, setDeleteForm] = useState({ password: '', confirm: '' });
  const [emailLoading, setEmailLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const VALID_SORT_KEYS = new Set([
    'id', 'item', 'type', 'room', 'container',
    'year_purchased', 'quantity', 'cost_cad', 'condition', 'certainty', 'timestamp',
  ]);
  const getInitialSort = () => {
    try {
      const saved = JSON.parse(localStorage.getItem('expatory_sort'));
      if (saved && VALID_SORT_KEYS.has(saved.key)) return saved;
    } catch (_) {}
    return { key: 'id', direction: 'desc' };
  };
  const [sortConfig, setSortConfig] = useState(getInitialSort);
  const [sortLoading, setSortLoading] = useState(false);
  const [colFilters, setColFilters] = useState({ item: '', type: '', room: '', container: '', condition: '' });
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const LIMIT = 100;
  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [showImportWizard, setShowImportWizard] = useState(false);

  const photoInputRef = useRef(null);
  const videoInputRef = useRef(null);
  const fileInputRef = useRef(null);
  const sentinelRef = useRef(null);
  // Stores the target filteredAndSortedInventory index to jump to after a server fetch
  // triggered by modal navigation at the last loaded item boundary.
  const pendingNavIndexRef = useRef(null);

  const dismissOnboarding = () => {
    localStorage.setItem('expatory_onboarded', '1');
    setShowOnboarding(false);
  };

  const handleLogout = React.useCallback(() => {
      localStorage.removeItem('token');
      localStorage.removeItem('userRole');
      setToken(null);
      setUserRole(null);
      setViewMode('table');
      // Clear stale Turnstile state so the useEffect re-renders the widget on login screen
      window._pendingTurnstileToken = null;
      setTurnstileToken(null);
      setTurnstileStatus('waiting');
  }, [setTurnstileToken, setTurnstileStatus]);

  const authenticatedFetch = React.useCallback(async (url, options = {}) => {
      const currentToken = token || localStorage.getItem('token');
      const headers = { ...options.headers, 'Authorization': `Bearer ${currentToken}`, 'ngrok-skip-browser-warning': '1' };
      const res = await fetch(url, { ...options, headers });
      if (res.status === 401) { handleLogout(); }
      return res;
  }, [token, handleLogout]);

  const checkSetup = React.useCallback(async () => {
      const controller = new AbortController();
      const id = setTimeout(() => controller.abort(), 5000);
      try {
          const res = await fetch(`${API_BASE}/setup-check`, { headers: { 'ngrok-skip-browser-warning': '1' }, signal: controller.signal });
          clearTimeout(id);
          const text = await res.text();
          try {
              const data = JSON.parse(text);
              setSetupRequired(data.setup_required);
              setConnectionStatus({ status: 'online', details: t.serverReachable });
          } catch (_) {
              setConnectionStatus({ status: 'offline', details: `HTTP ${res.status} — ${text.slice(0, 120)}` });
          }
      } catch (e) {
          setConnectionStatus({ status: 'offline', details: e.message === 'signal timed out' ? t.connectionTimeout : e.message });
      } finally { setAuthLoading(false); }
  }, [t.serverReachable, t.connectionTimeout]);

  const fetchSettings = React.useCallback(async () => {
    try {
        const roomsRes = await authenticatedFetch(`${API_BASE}/settings/rooms`);
        if (roomsRes.ok) setRoomList(await roomsRes.json());
        const containersRes = await authenticatedFetch(`${API_BASE}/settings/containers`);
        if (containersRes.ok) setContainerList(await containersRes.json());
        const currRes = await authenticatedFetch(`${API_BASE}/settings/currency`);
        if (currRes.ok) setCurrency(await currRes.json() || 'CAD');
        const locRes = await authenticatedFetch(`${API_BASE}/settings/location`);
        if (locRes.ok) setLocation(await locRes.json() || 'Canada');
        const langRes = await authenticatedFetch(`${API_BASE}/settings/language`);
        if (langRes.ok) setLanguage(await langRes.json() || 'en');
        const themeRes = await authenticatedFetch(`${API_BASE}/settings/theme`);
        if (themeRes.ok) setTheme(await themeRes.json() || 'light');
    } catch (e) { console.error(e); }
  }, [authenticatedFetch]);

  const fetchStats = React.useCallback(async () => {
    try {
      let url = `${API_BASE}/inventory/stats`;
      const params = new URLSearchParams();
      if (searchTerm) params.append('search', searchTerm);
      if (selectedCategory && selectedCategory !== 'All') params.append('type', selectedCategory);
      if (colFilters.item) params.append('col_item', colFilters.item);
      if (colFilters.type) params.append('col_type', colFilters.type);
      if (colFilters.room) params.append('col_room', colFilters.room);
      if (colFilters.container) params.append('col_container', colFilters.container);
      if (colFilters.condition) params.append('col_condition', colFilters.condition);
      if (params.toString()) url += `?${params.toString()}`;
      const response = await authenticatedFetch(url);
      if (!response.ok) throw new Error(`${response.status}`);
      const data = await response.json();
      setStats({ total_value: data.total_value || 0, item_count: data.item_count || 0, category_distribution: data.category_distribution || {} });
      setStatsError(false);
    } catch (err) { console.error('fetchStats:', err); setStatsError(true); }
  }, [searchTerm, selectedCategory, colFilters, authenticatedFetch]);

  const fetchAnalysisTasks = React.useCallback(async () => {
    if (!token) return;
    try {
        const res = await authenticatedFetch(`${API_BASE}/analysis-tasks`);
        if (res.ok) setAnalysisTasks(await res.json());
    } catch (e) { console.error(e); }
  }, [token, authenticatedFetch]);

  useEffect(() => {
    if (token) {
        fetchAnalysisTasks();
        const interval = setInterval(fetchAnalysisTasks, 5000);
        return () => clearInterval(interval);
    }
  }, [token, fetchAnalysisTasks]);

  const fetchInventory = React.useCallback(async (reset = false) => {
    const currentOffset = reset ? 0 : offset;
    try {
      setLoadingMore(true);
      if (reset) setSortLoading(true);
      let url = `${API_BASE}/inventory?limit=${LIMIT}&offset=${currentOffset}`;
      if (searchTerm) url += `&search=${encodeURIComponent(searchTerm)}`;
      if (selectedCategory && selectedCategory !== 'All') url += `&type=${encodeURIComponent(selectedCategory)}`;
      url += `&sort_by=${encodeURIComponent(sortConfig.key)}&sort_dir=${encodeURIComponent(sortConfig.direction)}`;
      if (colFilters.item) url += `&col_item=${encodeURIComponent(colFilters.item)}`;
      if (colFilters.type) url += `&col_type=${encodeURIComponent(colFilters.type)}`;
      if (colFilters.room) url += `&col_room=${encodeURIComponent(colFilters.room)}`;
      if (colFilters.container) url += `&col_container=${encodeURIComponent(colFilters.container)}`;
      if (colFilters.condition) url += `&col_condition=${encodeURIComponent(colFilters.condition)}`;
      const response = await authenticatedFetch(url);
      const data = await response.json();
      if (data && Array.isArray(data.items)) {
        if (reset) {
            setInventory(data.items);
            setEditedInventory(data.items);
            setOffset(LIMIT);
            setHasMore(data.items.length === LIMIT);
        } else {
            setInventory(prev => [...prev, ...data.items]);
            setEditedInventory(prev => [...prev, ...data.items]);
            setOffset(currentOffset + LIMIT);
            setHasMore(data.items.length === LIMIT);
            return data.items.length;
        }
      }
    } catch (err) { console.error(err); }
    finally { setLoadingMore(false); setSortLoading(false); }
  }, [offset, searchTerm, selectedCategory, sortConfig, colFilters, authenticatedFetch]);

  useEffect(() => { checkSetup(); }, [checkSetup]);
  const settingsLoadedRef = useRef(false);
  useEffect(() => {
    if (token && !settingsLoadedRef.current) { settingsLoadedRef.current = true; fetchSettings(); }
    if (!token) settingsLoadedRef.current = false;
  }, [token, fetchSettings]);

  useEffect(() => { if (token) { fetchInventory(true); fetchStats(); } }, [token, searchTerm, selectedCategory, sortConfig, colFilters]);

  const fetchAdminUsers = async () => {
      setAdminLoading(true);
      try {
          const res = await authenticatedFetch(`${API_BASE}/admin/users`);
          if (res.ok) setAdminUsers(await res.json());
      } finally { setAdminLoading(false); }
  };

  const deleteUser = async (userId) => {
      if (!window.confirm(t.adminDeleteUserConfirm)) return;
      try {
          const res = await authenticatedFetch(`${API_BASE}/admin/users/${userId}`, { method: 'DELETE' });
          if (res.ok) { await fetchAdminUsers(); alert(t.adminUserDeleted); }
      } catch (e) { alert(t.adminDeleteFailed); }
  };


  const saveSettings = async (key, value) => {
      try {
          const res = await authenticatedFetch(`${API_BASE}/settings/${key}`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(value) });
          if (res.ok && (key === 'rooms' || key === 'containers')) await fetchSettings(); 
      } catch (e) { alert(e.message); }
  };

  const handleChangePassword = async (e) => {
      e.preventDefault();
      if (passwordForm.next !== passwordForm.confirm) { alert(t.passwordMismatch); return; }
      setPasswordLoading(true);
      try {
          const res = await authenticatedFetch(`${API_BASE}/change-password`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ current_password: passwordForm.current, new_password: passwordForm.next }) });
          if (!res.ok) throw new Error("Update failed");
          alert(t.passwordUpdated);
          setPasswordForm({ current: '', next: '', confirm: '' });
      } catch (e) { alert(e.message); } finally { setPasswordLoading(false); }
  };

  const handleEmailChange = async (e) => {
      e.preventDefault();
      setEmailLoading(true);
      try {
          const res = await authenticatedFetch(`${API_BASE}/change-email`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ password: emailForm.password, new_email: emailForm.next }) });
          if (!res.ok) throw new Error("Update failed");
          alert(t.emailUpdatedReconnect);
          handleLogout();
      } catch (e) { alert(e.message); } finally { setEmailLoading(false); }
  };

  const handleDeleteAccount = async (e) => {
      e.preventDefault();
      if (deleteForm.confirm !== 'DELETE') { alert(t.typeDeleteConfirmPrompt); return; }
      if (!window.confirm(t.confirmDeleteEverything)) return;
      setDeleteLoading(true);
      try {
          const res = await authenticatedFetch(`${API_BASE}/delete-account`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ password: deleteForm.password }) });
          if (res.ok) { alert(t.accountDeleted); handleLogout(); }
      } catch (e) { alert(e.message); } finally { setDeleteLoading(false); }
  };



  useEffect(() => { document.body.setAttribute('data-bs-theme', theme); localStorage.setItem('theme', theme); }, [theme]);

  const toggleTheme = () => { const newTheme = theme === 'light' ? 'dark' : 'light'; setTheme(newTheme); saveSettings('theme', newTheme); };

  const handleExport = () => setShowExcelModal(true);

  const handleExportExcel = async () => {
    setExcelExporting(true);
    try {
        const response = await authenticatedFetch(`${API_BASE}/inventory/export`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(excelOptions) });
        if (!response.ok) throw new Error(`Export failed (${response.status})`);
        const imagesDisabled = response.headers.get('X-Export-Images-Disabled') === 'true';
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a'); a.href = url; a.download = `inventory_${new Date().toISOString().split('T')[0]}.xlsx`;
        document.body.appendChild(a); a.click(); document.body.removeChild(a); window.URL.revokeObjectURL(url);
        setShowExcelModal(false);
        if (imagesDisabled) alert(t.exportImagesDisabled);
    } catch (e) { alert(t.exportFailed + e.message); }
    finally { setExcelExporting(false); }
  };

  const handleExportPDF = async () => {
    setPdfExporting(true);
    try {
        const response = await authenticatedFetch(`${API_BASE}/inventory/export-pdf`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(pdfOptions) });
        if (!response.ok) throw new Error(response.status === 429 ? t.tooManyRequests : "PDF Export failed");
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a'); a.href = url; a.download = `Expatory_Report_${new Date().toISOString().split('T')[0].replace(/-/g, '')}.pdf`;
        document.body.appendChild(a); a.click(); document.body.removeChild(a); window.URL.revokeObjectURL(url);
        setShowPdfModal(false);
    } catch (e) { alert(e.message); }
    finally { setPdfExporting(false); }
  };

  const handleDownloadTemplate = async () => {
    try {
        const response = await fetch(`${API_BASE}/inventory/template`);
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a'); a.href = url; a.download = "expatory_template.xlsx";
        document.body.appendChild(a); a.click(); window.URL.revokeObjectURL(url);
    } catch (e) { alert(e.message); }
  };

  const handleEraseAll = async () => {
      try {
          const response = await authenticatedFetch(`${API_BASE}/inventory/erase`, { method: 'POST' });
          if (response.ok) { await fetchInventory(true); await fetchStats(); setShowEraseConfirm(false); alert(t.eraseSuccess); }
      } catch (err) { alert(err.message); }
  };


  const requestSort = (key) => {
    setSortConfig(prev => {
      const next = {
        key,
        direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
      };
      localStorage.setItem('expatory_sort', JSON.stringify(next));
      return next;
    });
  };

  const filteredAndSortedInventory = React.useMemo(() => {
      let result = [...inventory];
      if (colFilters.item)      result = result.filter(i => i.item?.toLowerCase().includes(colFilters.item.toLowerCase()));
      if (colFilters.type)      result = result.filter(i => i.type === colFilters.type);
      if (colFilters.room)      result = result.filter(i => i.room === colFilters.room);
      if (colFilters.container) result = result.filter(i => i.container === colFilters.container);
      if (colFilters.condition) result = result.filter(i => i.condition === colFilters.condition);
      return result;
  }, [inventory, colFilters]);

  const editFilteredInventory = React.useMemo(() => {
      let result = [...editedInventory];
      if (colFilters.item)      result = result.filter(i => i.item?.toLowerCase().includes(colFilters.item.toLowerCase()));
      if (colFilters.type)      result = result.filter(i => i.type === colFilters.type);
      if (colFilters.room)      result = result.filter(i => i.room === colFilters.room);
      if (colFilters.container) result = result.filter(i => i.container === colFilters.container);
      if (colFilters.condition) result = result.filter(i => i.condition === colFilters.condition);
      return result;
  }, [editedInventory, colFilters]);

  const totalFiltered = filteredAndSortedInventory.length;
  const activeFilterCount = Object.values(colFilters).filter(Boolean).length;
  const clearColFilters = () => setColFilters({ item: '', type: '', room: '', container: '', condition: '' });
  const tableColCount = 8 + (isEditing ? 1 : 0) + (selectMode ? 1 : 0);
  const allVisibleSelected = filteredAndSortedInventory.length > 0 && filteredAndSortedInventory.every(i => selectedIds.has(i.id));

  const toggleSelectMode = () => {
    if (isEditing) return;
    if (selectMode) { setSelectMode(false); setSelectedIds(new Set()); }
    else setSelectMode(true);
  };

  const handleRowSelect = (id) => {
    setSelectedIds(prev => { const next = new Set(prev); next.has(id) ? next.delete(id) : next.add(id); return next; });
  };

  const handleSelectAll = () => {
    if (allVisibleSelected) {
      setSelectedIds(prev => { const next = new Set(prev); filteredAndSortedInventory.forEach(i => next.delete(i.id)); return next; });
    } else {
      setSelectedIds(prev => { const next = new Set(prev); filteredAndSortedInventory.forEach(i => next.add(i.id)); return next; });
    }
  };

  const handleBulkApply = async (fields) => {
    if (selectedIds.size === 0) return;
    try {
      const res = await authenticatedFetch(`${API_BASE}/inventory/bulk-update`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ item_ids: Array.from(selectedIds), fields })
      });
      if (res.ok) {
        const count = selectedIds.size;
        setInventory(prev => prev.map(item => selectedIds.has(item.id) ? { ...item, ...fields } : item));
        setSelectedIds(new Set());
        setSelectMode(false);
        alert(t.bulkApplied.replace('{n}', count));
      }
    } catch (err) { alert(err.message); }
  };

  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) return;
    try {
      const ids = Array.from(selectedIds);
      const res = await authenticatedFetch(`${API_BASE}/inventory/bulk-delete`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ item_ids: ids })
      });
      if (res.ok) {
        const count = ids.length;
        setInventory(prev => prev.filter(item => !selectedIds.has(item.id)));
        setSelectedIds(new Set());
        setSelectMode(false);
        await fetchStats();
        alert(t.bulkDeleted.replace('{n}', count));
      }
    } catch (err) { alert(err.message); }
  };

  // After a server fetch triggered by modal forward-navigation at the boundary of loaded items,
  // open the target item once filteredAndSortedInventory grows to include it.
  useEffect(() => {
    if (pendingNavIndexRef.current === null) return;
    const target = pendingNavIndexRef.current;
    if (target < filteredAndSortedInventory.length) {
      handleSelectItem(filteredAndSortedInventory[target]);
      pendingNavIndexRef.current = null;
    } else if (!hasMore) {
      pendingNavIndexRef.current = null;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inventory]);

  // IntersectionObserver: load next batch when sentinel scrolls into view
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting && hasMore && !loadingMore) fetchInventory(false); },
      { rootMargin: '300px' }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMore, loadingMore, fetchInventory]);

  const toggleEditMode = () => { if (selectMode) { setSelectMode(false); setSelectedIds(new Set()); } if (isEditing) { setEditedInventory(inventory); setDirtyItemIds(new Set()); setIsEditing(false); } else { setEditedInventory(JSON.parse(JSON.stringify(inventory))); setDirtyItemIds(new Set()); setIsEditing(true); } };

  const handleEditChange = (id, field, value) => {
    setEditedInventory(prev => prev.map(item => item.id === id ? { ...item, [field]: value } : item));
    if (id) setDirtyItemIds(prev => new Set(prev).add(id));
  };

  const saveChanges = async () => {
    const changedItems = editedInventory.filter(item => dirtyItemIds.has(item.id));
    if (changedItems.length === 0) { setIsEditing(false); return; }
    try {
        const response = await authenticatedFetch(`${API_BASE}/inventory/update`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(changedItems) });
        if (response.ok) { setInventory(editedInventory); setDirtyItemIds(new Set()); await fetchStats(); setIsEditing(false); alert(t.updateSuccess); }
    } catch (err) { alert(err.message); }
  };

  const handleDeleteItem = async (id) => {
      if (!window.confirm(t.confirmDelete)) return;
      const y = window.scrollY;
      try {
          const response = await authenticatedFetch(`${API_BASE}/inventory/delete`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ item_id: id }) });
          if (response.ok) {
            setInventory(prev => prev.filter(i => i.id !== id));
            setShowDetail(false);
            await fetchStats();
            requestAnimationFrame(() => window.scrollTo(0, y));
          }
      } catch (err) { alert(err.message); }
  };

  const handleSelectItem = (item) => { setSelectedItem({ ...item, viewingOriginal: true }); setShowDetail(true); };


  const saveManualEntry = async () => {
      if (!selectedItem.item) { alert(t.itemNameRequired); return; }
      try {
          const response = await authenticatedFetch(`${API_BASE}/inventory/add`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify([selectedItem]) });
          if (response.ok) { await fetchInventory(true); await fetchStats(); setShowManualModal(false); setSelectedItem(null); }
      } catch (err) { alert(err.message); }
  };

  const runBackgroundAnalysis = async (filePath, scanId, maxCalls) => {
    let foundItems = [];
    try {
      const response = await fetch(`${API_BASE}/analyze`, { method: 'POST', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }, body: JSON.stringify({ file_path: filePath, max_calls: maxCalls }) });
      if (!response.ok) throw new Error('Analysis request failed');
      const reader = response.body.getReader(); const decoder = new TextDecoder(); let buffer = '';
      while (true) {
        const { value, done } = await reader.read(); if (done) break;
        buffer += decoder.decode(value, { stream: true }); const lines = buffer.split('\n\n'); buffer = lines.pop();
        for (const line of lines) {
          if (line.startsWith('data: ')) {
            const jsonStr = line.slice(6); if (jsonStr === '[DONE]') break;
            try {
              const data = JSON.parse(jsonStr);
              if (data.type === 'task_id') { setActiveTaskId(data.task_id); setBgScans(prev => prev.map(s => s.id === scanId ? { ...s, taskId: data.task_id } : s)); }
              else if (data.type === 'status') { setBgScans(prev => prev.map(s => s.id === scanId ? { ...s, status: data.message } : s)); }
              else if (data.type === 'result') { const itemsWithSource = data.items.map(item => ({ ...item, source_file: filePath })); setNewItems(prev => [...prev, ...itemsWithSource]); foundItems.push(...itemsWithSource); }
              else if (data.type === 'error') { setBgScans(prev => prev.map(s => s.id === scanId ? { ...s, failed: true, failMessage: data.message } : s)); return; }
            } catch (e) {}
          }
        }
      }
      if (foundItems.length === 0) setNewItems(prev => [...prev, { item: t.unknownItem, type: t.uncategorized, cost_cad: 0, condition: 'Good', quantity: 1, source_file: filePath, certainty: 'None' }]);
      setBgScans(prev => prev.filter(s => s.id !== scanId));
    } catch (err) {
      setBgScans(prev => prev.map(s => s.id === scanId ? { ...s, failed: true, failMessage: err.message } : s));
    }
  };

  const handleFileChange = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    setUploadProgress(0); setUploading(true); setStatus(t.uploadingEvidence); setError(null); setUploadFailed(false);
    const formData = new FormData(); formData.append('file', file);
    try {
      const maxCalls = scanPrecision === 'exhaustive' ? 30 : 12;
      const uploadData = await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('POST', `${API_BASE}/upload-simple`);
        xhr.setRequestHeader('Authorization', `Bearer ${token}`);
        xhr.timeout = 120000;
        xhr.upload.onprogress = (e) => { if (e.lengthComputable) setUploadProgress(Math.round((e.loaded * 100) / e.total)); };
        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            resolve(JSON.parse(xhr.responseText));
          } else {
            let msg = `Upload failed (HTTP ${xhr.status})`;
            try { msg = JSON.parse(xhr.responseText)?.detail || msg; } catch (_) {}
            reject(new Error(msg));
          }
        };
        xhr.onerror   = () => reject(new Error('Network error during upload'));
        xhr.ontimeout = () => reject(new Error('Upload timed out — try a smaller file or check your connection'));
        xhr.onabort   = () => reject(new Error('Upload was cancelled'));
        xhr.send(formData);
      });
      const serverFilePath = uploadData.file_path;
      setLastUploadedFile(serverFilePath);
      setUploading(false);
      setStatus('');
      const scanId = `${Date.now()}-${Math.random()}`;
      setBgScans(prev => [...prev, { id: scanId, taskId: null, filePath: serverFilePath, status: t.initializingAiAnalysis, failed: false, failMessage: '' }]);
      runBackgroundAnalysis(serverFilePath, scanId, maxCalls);
    } catch (err) { setError(err.message); setUploading(false); setUploadFailed(true); }
    finally { if (photoInputRef.current) photoInputRef.current.value = ''; if (videoInputRef.current) videoInputRef.current.value = ''; if (fileInputRef.current) fileInputRef.current.value = ''; }
  };

  const retryBgScan = (scanId, filePath) => {
    const maxCalls = scanPrecision === 'exhaustive' ? 30 : 12;
    const newScanId = `${Date.now()}-${Math.random()}`;
    setBgScans(prev => [...prev.filter(s => s.id !== scanId), { id: newScanId, taskId: null, filePath, status: t.initializingAiAnalysis, failed: false, failMessage: '' }]);
    runBackgroundAnalysis(filePath, newScanId, maxCalls);
  };


  const navigateItem = async (direction) => {
    if (!selectedItem) return;
    const idx = filteredAndSortedInventory.findIndex(i => i.id === selectedItem.id);
    if (idx === -1) return;
    const newIdx = idx + direction;
    if (newIdx >= 0 && newIdx < filteredAndSortedInventory.length) {
      handleSelectItem(filteredAndSortedInventory[newIdx]);
    } else if (direction === 1 && hasMore) {
      if (loadingMore) return;
      // Past the last loaded item — fetch the next batch, then complete via useEffect
      pendingNavIndexRef.current = newIdx;
      await fetchInventory(false);
    }
  };

  const handleForgotPassword = async () => {
    setAuthLoading2(true);
    try {
      await fetch(`${API_BASE}/forgot-password`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ username: forgotEmail }) });
    } catch (_) {}
    finally { setForgotSubmitted(true); setAuthLoading2(false); }
  };

  const handleResetPassword = async () => {
    setResetError('');
    if (resetNewPassword !== resetConfirmPassword) { setResetError(t.passwordsDoNotMatch); return; }
    setAuthLoading2(true);
    try {
      const res = await fetch(`${API_BASE}/reset-password`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ token: resetToken, new_password: resetNewPassword }) });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail);
      setResetSuccess(true);
      setTimeout(() => {
        setAuthMode(null); setResetSuccess(false); setResetToken('');
        setResetNewPassword(''); setResetConfirmPassword(''); setResetError('');
      }, 3000);
    } catch (err) { setResetError(err.message || t.resetLinkExpired); }
    finally { setAuthLoading2(false); }
  };

  const handleLogin = async (e) => {
      e.preventDefault(); if (!turnstileToken) { alert(t.waitForVerification); return; }
      const user = e.target.username.value, pass = e.target.password.value, hp = e.target.hp_field.value;
      const formData = new URLSearchParams(); formData.append('username', user); formData.append('password', pass); formData.append('hp_field', hp); formData.append('turnstile_token', turnstileToken);
      setAuthLoading2(true);
      try {
          const res = await fetch(`${API_BASE}/token`, { method: 'POST', body: formData });
          if (!res.ok) { const data = await res.json(); throw new Error(data.detail?.includes("Email not verified") ? t.emailNotVerified : t.invalidLogin); }
          const data = await res.json(); setTurnstileToken(null); localStorage.setItem('token', data.access_token); localStorage.setItem('userRole', data.role); setToken(data.access_token); setUserRole(data.role); if (data.role === 'admin') { setViewMode('admin'); fetchAdminUsers(); } else if (!localStorage.getItem('expatory_onboarded')) { setShowOnboarding(true); }
      } catch (err) { if (window.turnstile) window.turnstile.reset('#turnstile-widget'); setTurnstileToken(null); alert(err.message); }
      finally { setAuthLoading2(false); }
  };

  const handleRegister = async (e) => {
      e.preventDefault(); if (!turnstileToken) { alert(t.waitForVerification); return; }
      const user = e.target.username.value, pass = e.target.password.value, confirmPass = e.target.confirm_password.value, hp = e.target.hp_field.value;
      if (pass !== confirmPass) { alert(t.passwordsDoNotMatch); return; }
      setAuthLoading2(true);
      try {
          const res = await fetch(`${API_BASE}/register`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ username: user, password: pass, hp_field: hp, turnstile_token: turnstileToken }) });
          if (!res.ok) { const err = await res.json(); throw new Error(err.detail || t.registrationFailed); }
          alert(t.registrationSuccess); setRegistering(false); setTurnstileToken(null);
      } catch (err) { if (window.turnstile) window.turnstile.reset('#turnstile-widget'); setTurnstileToken(null); alert(err.message); }
      finally { setAuthLoading2(false); }
  };

  const handleSetup = async (e) => {
      e.preventDefault(); if (!turnstileToken) { alert(t.waitForVerification); return; }
      const user = e.target.username.value, pass = e.target.password.value, confirmPass = e.target.confirm_password.value, hp = e.target.hp_field.value;
      if (pass !== confirmPass) { alert(t.passwordsDoNotMatch); return; }
      try {
          const res = await fetch(`${API_BASE}/setup`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ username: user, password: pass, hp_field: hp, turnstile_token: turnstileToken }) });
          if (!res.ok) { const errorData = await res.json(); throw new Error(errorData.detail || t.setupFailed); }
          setSetupRequired(false); setTurnstileToken(null); alert(t.adminCreatedSuccess);
      } catch (err) { if (window.turnstile) window.turnstile.reset('#turnstile-widget'); setTurnstileToken(null); alert(err.message); }
  };

  const AdminDashboard = () => (
      <div>
          <div className="d-flex justify-content-between align-items-center mb-4">
              <h4><FaCog className="me-2"/> {t.adminPanel}</h4>
              <Button variant="outline-secondary" size="sm" onClick={fetchAdminUsers} disabled={adminLoading}>{adminLoading ? <Spinner animation="border" size="sm"/> : <FaRedo className="me-2"/>} {t.refreshList}</Button>
          </div>
          <Card className="shadow-sm border-0"><Table responsive hover striped className="mb-0 align-middle"><thead><tr><th>{t.userEmail}</th><th>Role</th><th>{t.created}</th><th>{t.apiUsage}</th><th>{t.estCost}</th><th>{t.totalItems}</th><th>{t.actions}</th></tr></thead><tbody>{adminUsers.map(u => (<tr key={u.id}><td className="fw-bold">{u.email}</td><td><Badge bg={u.role === 'admin' ? 'danger' : 'info'}>{u.role}</Badge></td><td className="small text-muted">{new Date(u.created_at).toLocaleDateString()}</td><td><Badge bg={u.api_usage > 100 ? 'danger' : u.api_usage > 50 ? 'warning' : 'success'}>{u.api_usage} calls</Badge></td><td><code className="small">${(u.total_cost || 0).toFixed(5)}</code></td><td>{u.item_count}</td><td><Button variant="link" className="text-danger p-0" onClick={() => deleteUser(u.id)} disabled={u.role === 'admin'}><FaTrash /></Button></td></tr>))}</tbody></Table></Card>
      </div>
  );

  const handleReviewTask = async (taskId) => {
    try {
        const res = await authenticatedFetch(`${API_BASE}/analysis-tasks/${taskId}`);
        if (res.ok) { const data = await res.json(); if (data.status === 'COMPLETED' && data.result) { setNewItems(data.result.map(item => ({ ...item }))); setActiveTaskId(taskId); setViewMode('table'); window.scrollTo({ top: 0, behavior: 'smooth' }); } }
    } catch (e) { console.error(e); }
  };

  const deleteAnalysisTask = async (taskId) => {
    try {
        const res = await authenticatedFetch(`${API_BASE}/analysis-tasks/${taskId}`, { method: 'DELETE' });
        if (res.ok) { fetchAnalysisTasks(); if (activeTaskId === taskId) { setActiveTaskId(null); setNewItems([]); } }
    } catch (e) { console.error(e); }
  };

  const handleRetryTask = async (taskId) => { try { const res = await authenticatedFetch(`${API_BASE}/analysis-tasks/${taskId}/retry`, { method: 'POST' }); if (res.ok) fetchAnalysisTasks(); } catch (e) { console.error(e); } };

  const handleTurnstileReset = () => {
    window._pendingTurnstileToken = null;
    setTurnstileToken(null);
    setTurnstileStatus('waiting');
    const container = document.getElementById('turnstile-widget');
    if (container) container.innerHTML = '';
    if (window.turnstile && container) {
      try {
        window.turnstile.render('#turnstile-widget', {
          sitekey: TURNSTILE_SITEKEY,
          callback: window.onTurnstileSuccess,
          theme: theme
        });
      } catch (e) { console.warn("Turnstile Render Error:", e); }
    }
  };

  if (authLoading) return <div className="splash-container"><img src={logo} className="splash-logo" alt="Logo" /><div className="splash-title">Expatory</div><div className="mt-3 text-muted small animate-pulse">{t.initializingSecureConnection}</div></div>;

  if (setupRequired) return (
    <Container className="d-flex justify-content-center align-items-center vh-100" style={{ background: 'var(--bs-body-bg)' }}>
      <Card className="border-0 shadow-lg" style={{ maxWidth: '420px', width: '100%', borderRadius: '16px', overflow: 'hidden', transition: 'all 0.2s ease' }}>
        <div style={{ background: 'linear-gradient(135deg, #0d6efd 0%, #0a58ca 100%)', padding: '28px 32px 24px', textAlign: 'center', position: 'relative' }}>
          <button type="button" onClick={() => { const l = language === 'en' ? 'fr' : 'en'; setLanguage(l); localStorage.setItem('language', l); }} style={{ position: 'absolute', top: '12px', right: '14px', background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.35)', borderRadius: '6px', color: '#fff', fontSize: '0.72rem', fontWeight: 700, padding: '3px 8px', cursor: 'pointer', letterSpacing: '0.06em' }}>{language === 'en' ? 'FR' : 'EN'}</button>
          <img src={logo} alt="Expatory" style={{ width: '52px', height: '52px', marginBottom: '12px', filter: 'brightness(0) invert(1)' }} />
          <h2 className="mb-0 fw-bold" style={{ color: '#fff', fontSize: '1.4rem', letterSpacing: '-0.3px' }}>{t.setupTitle}</h2>
        </div>
        <div className="p-4">
          <Form onSubmit={handleSetup}>
            <div style={{ display: 'none' }}><input type="text" name="hp_field" tabIndex="-1" autoComplete="off" /></div>
            <Form.Group className="mb-3"><Form.Label className="fw-semibold small">{t.chooseEmailAdmin}</Form.Label><Form.Control name="username" type="email" required style={{ borderRadius: '8px', padding: '10px 14px' }} /></Form.Group>
            <Form.Group className="mb-3"><Form.Label className="fw-semibold small">{t.choosePassword}</Form.Label><Form.Control name="password" type="password" required style={{ borderRadius: '8px', padding: '10px 14px' }} /><Form.Text className="text-muted small">{t.passwordComplexityHint}</Form.Text></Form.Group>
            <Form.Group className="mb-4"><Form.Label className="fw-semibold small">{t.confirmPassword}</Form.Label><Form.Control name="confirm_password" type="password" required style={{ borderRadius: '8px', padding: '10px 14px' }} /></Form.Group>
            <div className="d-flex flex-column align-items-center mb-4" style={{ minHeight: '76px' }}>
              <div id="turnstile-widget"></div>
              {turnstileStatus === 'timeout' && (
                <button type="button" className="btn btn-link btn-sm text-warning p-0 mt-2" onClick={handleTurnstileReset} style={{ fontSize: '0.82rem' }}>
                  <FaRedo className="me-1" style={{ fontSize: '0.7rem' }} />{t.retryVerification}
                </button>
              )}
            </div>
            <Button variant="primary" type="submit" className="w-100 fw-semibold" style={{ borderRadius: '8px', padding: '11px', fontSize: '0.95rem' }}>{t.setupButton}</Button>
          </Form>
        </div>
      </Card>
    </Container>
  );

  if (!token) return (
    <>
    <Container className="d-flex justify-content-center align-items-center" style={{ minHeight: '100vh', padding: '24px 16px', background: 'var(--bs-body-bg)' }}>
      <Card className="border-0 shadow-lg w-100" style={{ maxWidth: '420px', borderRadius: '16px', overflow: 'hidden', transition: 'all 0.2s ease' }}>
        {/* Brand header */}
        <div style={{ background: 'linear-gradient(135deg, #0d6efd 0%, #0a58ca 100%)', padding: '28px 32px 24px', textAlign: 'center', position: 'relative' }}>
          <button type="button" onClick={() => { const l = language === 'en' ? 'fr' : 'en'; setLanguage(l); localStorage.setItem('language', l); }} style={{ position: 'absolute', top: '12px', right: '14px', background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.35)', borderRadius: '6px', color: '#fff', fontSize: '0.72rem', fontWeight: 700, padding: '3px 8px', cursor: 'pointer', letterSpacing: '0.06em' }}>{language === 'en' ? 'FR' : 'EN'}</button>
          <img src={logo} alt="Expatory" style={{ width: '52px', height: '52px', marginBottom: '12px', filter: 'brightness(0) invert(1)' }} />
          <h2 className="mb-1 fw-bold" style={{ color: '#fff', fontSize: '1.5rem', letterSpacing: '-0.4px' }}>Expatory</h2>
          <p className="mb-0" style={{ color: 'rgba(255,255,255,0.75)', fontSize: '0.82rem' }}>{t.subtitle}</p>
        </div>

        <div className="p-4 pb-3">

          {/* ── Forgot Password panel ── */}
          {authMode === 'forgot' && (
            <div>
              <h5 className="fw-bold mb-1">{t.forgotPasswordTitle}</h5>
              <p className="text-muted small mb-3">{t.forgotPasswordDesc}</p>
              {!forgotSubmitted ? (
                <>
                  <Form.Group className="mb-3">
                    <Form.Label className="fw-semibold small">{t.emailAddress}</Form.Label>
                    <Form.Control type="email" value={forgotEmail} onChange={e => setForgotEmail(e.target.value)} placeholder="name@example.com" style={{ borderRadius: '8px', padding: '10px 14px' }} />
                  </Form.Group>
                  <Button variant="primary" className="w-100 fw-semibold mb-3" style={{ borderRadius: '8px', padding: '11px' }} onClick={handleForgotPassword} disabled={authLoading2 || !forgotEmail}>
                    {authLoading2 ? <><Spinner animation="border" size="sm" className="me-2" style={{ width: '14px', height: '14px' }} />{t.sendResetLink}</> : t.sendResetLink}
                  </Button>
                </>
              ) : (
                <Alert variant="success" className="small">{t.resetEmailSent}</Alert>
              )}
              <div className="text-center">
                <Button variant="link" size="sm" className="text-primary" style={{ fontSize: '0.84rem', textDecoration: 'none' }} onClick={() => { setAuthMode(null); setForgotSubmitted(false); setForgotEmail(''); }}>
                  ← {t.backToLogin}
                </Button>
              </div>
            </div>
          )}

          {/* ── Reset Password panel ── */}
          {authMode === 'reset' && (
            <div>
              <h5 className="fw-bold mb-1">{t.resetPasswordTitle}</h5>
              <p className="text-muted small mb-3">{t.resetPasswordDesc}</p>
              {!resetSuccess ? (
                <>
                  {resetError && <Alert variant="danger" className="small py-2">{resetError}</Alert>}
                  <Form.Group className="mb-3">
                    <Form.Label className="fw-semibold small">{t.newPasswordLabel}</Form.Label>
                    <Form.Control type="password" value={resetNewPassword} onChange={e => setResetNewPassword(e.target.value)} placeholder={t.passwordPlaceholder} autoComplete="new-password" style={{ borderRadius: '8px', padding: '10px 14px' }} />
                    <Form.Text className="text-muted small">{t.passwordComplexityHint}</Form.Text>
                  </Form.Group>
                  <Form.Group className="mb-4">
                    <Form.Label className="fw-semibold small">{t.confirmNewPassword}</Form.Label>
                    <Form.Control type="password" value={resetConfirmPassword} onChange={e => setResetConfirmPassword(e.target.value)} placeholder={t.passwordPlaceholder} autoComplete="new-password" style={{ borderRadius: '8px', padding: '10px 14px' }} />
                  </Form.Group>
                  <Button variant="primary" className="w-100 fw-semibold" style={{ borderRadius: '8px', padding: '11px' }} onClick={handleResetPassword} disabled={authLoading2 || !resetNewPassword || !resetConfirmPassword}>
                    {authLoading2 ? <><Spinner animation="border" size="sm" className="me-2" style={{ width: '14px', height: '14px' }} />{t.updatePassword}</> : t.updatePassword}
                  </Button>
                </>
              ) : (
                <Alert variant="success" className="small">{t.resetSuccess}</Alert>
              )}
              {!resetSuccess && (
                <div className="text-center mt-3">
                  <Button variant="link" size="sm" className="text-primary" style={{ fontSize: '0.84rem', textDecoration: 'none' }} onClick={() => { setAuthMode(null); setResetError(''); setResetNewPassword(''); setResetConfirmPassword(''); }}>
                    ← {t.backToLogin}
                  </Button>
                </div>
              )}
            </div>
          )}

          {/* ── Login / Register form ── */}
          {!authMode && (
          <>
          <Form onSubmit={registering ? handleRegister : handleLogin}>
            <div style={{ display: 'none' }}><input type="text" name="hp_field" tabIndex="-1" autoComplete="off" /></div>

            <Form.Group className="mb-3">
              <Form.Label className="fw-semibold small">{t.emailAddress}</Form.Label>
              <Form.Control name="username" type="email" required placeholder="name@example.com" autoComplete="username" style={{ borderRadius: '8px', padding: '10px 14px' }} />
            </Form.Group>

            <Form.Group className="mb-1">
              <Form.Label className="fw-semibold small">{t.password}</Form.Label>
              <Form.Control name="password" type="password" required placeholder={t.passwordPlaceholder} autoComplete={registering ? "new-password" : "current-password"} style={{ borderRadius: '8px', padding: '10px 14px' }} />
              {registering && <Form.Text className="text-muted small">{t.passwordComplexityHint}</Form.Text>}
            </Form.Group>

            {/* Forgot password link — login mode only */}
            {!registering && (
              <div className="text-end mb-2">
                <button type="button" onClick={() => { setAuthMode('forgot'); setForgotEmail(''); setForgotSubmitted(false); }} style={{ background: 'none', border: 'none', padding: 0, color: '#0d6efd', fontSize: '0.78rem', cursor: 'pointer', textDecoration: 'none' }}>
                  {t.forgotPassword}
                </button>
              </div>
            )}

            <div style={{ overflow: 'hidden', maxHeight: registering ? '80px' : '0', transition: 'max-height 0.25s ease', marginBottom: registering ? '12px' : '8px' }}>
              <Form.Group>
                <Form.Label className="fw-semibold small">{t.confirmPassword}</Form.Label>
                <Form.Control name="confirm_password" type="password" placeholder={t.passwordPlaceholder} autoComplete="new-password" style={{ borderRadius: '8px', padding: '10px 14px' }} />
              </Form.Group>
            </div>

            {/* Turnstile widget + status */}
            <div className="d-flex flex-column align-items-center my-3" style={{ minHeight: '72px' }}>
              <div id="turnstile-widget"></div>
              <div className="mt-2" style={{ minHeight: '24px', display: 'flex', alignItems: 'center' }}>
                {turnstileToken ? (
                  <span style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#198754', fontSize: '0.82rem', fontWeight: 600 }}>
                    <FaCheckCircle size={14} />{t.verified}
                  </span>
                ) : turnstileStatus === 'timeout' ? (
                  <button type="button" className="btn btn-link p-0" style={{ color: '#fd7e14', fontSize: '0.82rem', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '5px' }} onClick={handleTurnstileReset}>
                    <FaExclamationTriangle size={13} />{t.retryVerification}
                  </button>
                ) : (
                  <span className="animate-pulse" style={{ display: 'flex', alignItems: 'center', gap: '6px', color: 'var(--bs-secondary-color)', fontSize: '0.8rem' }}>
                    <FaShieldAlt size={12} />{t.verificationLoading}
                  </span>
                )}
              </div>
            </div>

            <Button
              variant="primary"
              type="submit"
              className="w-100 fw-semibold"
              disabled={authLoading2 || !turnstileToken}
              style={{ borderRadius: '8px', padding: '11px', fontSize: '0.95rem' }}
            >
              {authLoading2 ? (
                <><Spinner animation="border" size="sm" className="me-2" style={{ width: '14px', height: '14px' }} />{registering ? t.registering : t.loggingIn}</>
              ) : (
                registering ? t.registerButton : t.loginButton
              )}
            </Button>
          </Form>

          {/* Toggle login/register + UAP */}
          <div className="text-center mt-3">
            <Button variant="link" size="sm" className="text-primary" style={{ fontSize: '0.84rem', textDecoration: 'none' }} onClick={() => setRegistering(!registering)}>
              {registering ? t.alreadyHaveAccount : t.dontHaveAccount}
            </Button>
            <div className="mt-1">
              <Button variant="link" size="sm" className="text-muted" style={{ fontSize: '0.7rem', textDecoration: 'none' }} onClick={() => setShowUAPModal(true)}>{t.userAcceptancePolicy}</Button>
            </div>
          </div>
          </>
          )}

          {/* Server diagnostics — collapsed by default */}
          <div className="mt-3 pt-2 border-top">
            <details>
              <summary className="small text-muted fw-semibold" style={{ cursor: 'pointer', userSelect: 'none', listStyle: 'none', display: 'flex', alignItems: 'center', gap: '6px' }}>
                <FaCog size={11} />{t.advancedDiagnostics}
                <Badge bg={connectionStatus.status === 'online' ? 'success' : connectionStatus.status === 'error' ? 'danger' : 'warning'} className="ms-auto" style={{ fontSize: '0.6rem' }}>{connectionStatus.status.toUpperCase()}</Badge>
              </summary>
              <div className="mt-2">
                <div className="bg-light p-2 rounded border" style={{ fontSize: '0.7rem' }}>
                  <div className="text-truncate"><strong>Target:</strong> {API_BASE}</div>
                  <div className="text-danger mt-1"><strong>Status:</strong> {connectionStatus.details}</div>
                </div>
                <Row className="g-2 mt-1">
                  <Col xs={6}><Button variant="outline-secondary" size="sm" className="w-100" style={{ fontSize: '0.65rem' }} onClick={checkSetup}><FaRedo className="me-1" />{t.reTest}</Button></Col>
                  <Col xs={6}><Button variant="outline-primary" size="sm" className="w-100" style={{ fontSize: '0.65rem' }} onClick={() => window.open(API_ROOT, '_blank')}><FaExternalLinkAlt className="me-1" />{t.fixImages}</Button></Col>
                </Row>
              </div>
            </details>
          </div>
        </div>
      </Card>
    </Container>
    <Modal show={showUAPModal} onHide={() => setShowUAPModal(false)} size="lg" centered scrollable><Modal.Header closeButton><Modal.Title>{t.uapTitle}</Modal.Title></Modal.Header><Modal.Body><Alert variant="info" className="small">{t.uapSummary}</Alert>{[1,2,3,4,5,6,7,8,9,10,11].map(n => (<div className="mb-3" key={n}><h6>{t[`uapSection${n}Title`]}</h6><p className="small text-muted">{t[`uapSection${n}Content`]}</p></div>))}<div className="border-top pt-3 mt-3 small text-muted">Last Updated: March 10, 2026</div></Modal.Body><Modal.Footer><Button variant="primary" onClick={() => setShowUAPModal(false)}>{t.close}</Button></Modal.Footer></Modal>
    </>
  );

  const SortIcon = ({ colKey }) => {
    const active = sortConfig.key === colKey;
    if (!active) return <FaSort className="ms-1 text-muted opacity-50" size={11} />;
    return (
      <span className="ms-1" style={{
        display: 'inline-block',
        transform: sortConfig.direction === 'asc' ? 'rotate(0deg)' : 'rotate(180deg)',
        transition: 'transform 0.2s ease',
        color: 'var(--bs-primary)',
      }}>
        <FaSortUp size={11} />
      </span>
    );
  };

  const SORT_COL_LABELS = {
    item: t.item, type: t.type, room: t.room, container: t.storage,
    year_purchased: t.yearPurchased, quantity: t.qty, cost_cad: t.cost, condition: t.cond,
    certainty: 'AI', id: t.recentlyAdded, timestamp: t.recentlyAdded,
  };

  const SkeletonRow = ({ colCount }) => (
    <tr className="inventory-skeleton-row">
      {Array.from({ length: colCount }).map((_, i) => (
        <td key={i}><div className="skeleton-cell" /></td>
      ))}
    </tr>
  );

  return (
    <div className="App">
      <Container className="py-4" style={{ paddingBottom: '80px' }}>
      <div className="d-flex justify-content-between align-items-center mb-5">
        <div style={{ width: '40px' }}></div>
        <div className="text-center flex-grow-1">
            <h1 className="display-5 fw-bold text-primary mb-0">
                <img src={logo} alt="Expatory" style={{ width: '50px', height: '50px', verticalAlign: 'middle', marginRight: '10px' }} />
                {t.appTitle}
            </h1>
            <p className="text-muted mb-0">{t.subtitle}</p>
        </div>
        <div className="d-flex align-items-center">
        <Dropdown align="end" className="me-2">
            <Dropdown.Toggle variant="link" className="text-muted p-0 border-0 position-relative" id="tasks-dropdown">
                <FaRobot size={24} className={analysisTasks.some(t => t.status === 'PROCESSING' || t.status === 'PENDING') ? 'text-primary' : 'text-muted'} />
                {analysisTasks.some(t => t.status === 'COMPLETED' || t.status === 'FAILED') && (
                    <Badge pill bg="danger" className="position-absolute top-0 start-100 translate-middle" style={{ fontSize: '0.6rem' }}>
                        {analysisTasks.filter(t => t.status === 'COMPLETED' || t.status === 'FAILED').length}
                    </Badge>
                )}
            </Dropdown.Toggle>
            <Dropdown.Menu className="shadow-sm" style={{ minWidth: '280px', maxHeight: '400px', overflowY: 'auto' }}>
                <Dropdown.Header className="d-flex justify-content-between align-items-center">{t.analysisTasks}<Button variant="link" size="sm" onClick={fetchAnalysisTasks} className="p-0"><FaRedo size={12}/></Button></Dropdown.Header>
                {analysisTasks.length === 0 ? <Dropdown.Item text className="text-muted small">{t.noActiveTasks}</Dropdown.Item> : analysisTasks.map(task => (
                    <div key={task.id} className="px-3 py-2 border-bottom small">
                        <div className="d-flex justify-content-between mb-1"><span className="fw-bold">Task #{task.id}</span><div className="d-flex gap-2 align-items-center"><Badge bg={task.status === 'COMPLETED' ? 'success' : task.status === 'FAILED' ? 'danger' : 'primary'}>{task.status}</Badge><Button variant="link" className="text-danger p-0 ms-1" onClick={(e) => { e.stopPropagation(); deleteAnalysisTask(task.id); }}><FaTrash size={12}/></Button></div></div>
                        <div className="text-truncate text-muted mb-2" style={{ fontSize: '0.7rem' }}>{task.file_path.split('/').pop()}</div>
                        {task.status === 'COMPLETED' && <Button variant="primary" size="sm" className="w-100 py-0" style={{ fontSize: '0.7rem' }} onClick={() => handleReviewTask(task.id)}>{t.viewResults}</Button>}
                        {task.status === 'FAILED' && <Button variant="outline-danger" size="sm" className="w-100 py-0" style={{ fontSize: '0.7rem' }} onClick={() => handleRetryTask(task.id)}>{t.retryTask}</Button>}
                        {(task.status === 'PROCESSING' || task.status === 'PENDING') && <div className="text-primary fw-bold" style={{ fontSize: '0.65rem' }}><Spinner animation="border" size="sm" className="me-1" style={{ width: '10px', height: '10px' }} />{t.analyzingInBackground}</div>}
                    </div>
                ))}
            </Dropdown.Menu>
        </Dropdown>
        <Dropdown align="end">
            <Dropdown.Toggle variant="link" className="text-muted p-0 border-0" id="settings-dropdown"><FaCog size={24} /></Dropdown.Toggle>
            <Dropdown.Menu className="shadow-sm">
                <Dropdown.Header>Settings</Dropdown.Header>
                <Dropdown.Item onClick={toggleTheme}>{theme === 'light' ? <><FaMoon className="me-2"/> Dark Mode</> : <><FaSun className="me-2"/> Light Mode</>}</Dropdown.Item>
                {userRole === 'admin' && <Dropdown.Item onClick={() => { setViewMode(viewMode === 'admin' ? 'table' : 'admin'); if(viewMode !== 'admin') fetchAdminUsers(); }}><FaCog className="me-2 text-danger"/> {viewMode === 'admin' ? 'View Inventory' : 'User Control Panel'}</Dropdown.Item>}
                <Dropdown.Item onClick={handleExport}><FaFileExcel className="me-2 text-success"/> {t.export}</Dropdown.Item>
                <Dropdown.Item onClick={() => setShowPdfModal(true)}><FaFilePdf className="me-2 text-danger"/> {t.exportPdf}</Dropdown.Item>
                <Dropdown.Item onClick={() => setShowImportWizard(true)}><FaFileExcel className="me-2 text-primary"/> {t.import}</Dropdown.Item>
                <Dropdown.Item onClick={() => setShowSettingsModal(true)}><FaCog className="me-2"/> {t.settingsTitle}</Dropdown.Item>
                <Dropdown.Divider /><Dropdown.Item onClick={() => setShowTutorial(true)}><FaQuestionCircle className="me-2"/> {t.tutorial}</Dropdown.Item><Dropdown.Item onClick={() => setShowAbout(true)}><FaInfoCircle className="me-2"/> {t.about}</Dropdown.Item><Dropdown.Item onClick={() => { localStorage.removeItem('expatory_onboarded'); setShowOnboarding(true); }}><FaBoxOpen className="me-2"/> {t.showOnboarding}</Dropdown.Item><Dropdown.Divider /><Dropdown.Item onClick={handleLogout}><FaTimes className="me-2"/> {t.logout}</Dropdown.Item><Dropdown.Divider /><Dropdown.Item onClick={() => setShowEraseConfirm(true)} className="text-danger"><FaTrash className="me-2"/> {t.eraseData}</Dropdown.Item>
            </Dropdown.Menu>
        </Dropdown>
        </div>
      </div>

      {viewMode === 'admin' ? <AdminDashboard /> : (
          <>
      <Row className="justify-content-center mb-4"><Col md={8} lg={6}><Card className="shadow-sm border-0"><Card.Body className="text-center p-4">
              <input type="file" accept="image/*" onChange={handleFileChange} style={{ display: 'none' }} ref={photoInputRef} />
              <input type="file" accept="video/*" onChange={handleFileChange} style={{ display: 'none' }} ref={videoInputRef} />
              <input type="file" accept="image/*,video/*" onChange={handleFileChange} style={{ display: 'none' }} ref={fileInputRef} />
              {uploading ? <div className="py-3"><ProgressBar animated now={uploadProgress} label={`${uploadProgress}%`} className="mb-3" /><h5>{t.uploadingEvidence}</h5></div> : <>
                <div className="d-flex justify-content-center mb-3"><div className="btn-group border rounded-pill p-1 bg-body-tertiary shadow-sm" style={{ width: 'auto' }}><Button variant={scanPrecision === 'standard' ? 'primary' : 'link'} size="sm" className={`rounded-pill px-3 border-0 ${scanPrecision === 'standard' ? '' : 'text-muted'}`} onClick={() => setScanPrecision('standard')}>{t.standard}</Button><Button variant={scanPrecision === 'exhaustive' ? 'primary' : 'link'} size="sm" className={`rounded-pill px-3 border-0 ${scanPrecision === 'exhaustive' ? '' : 'text-muted'}`} onClick={() => setScanPrecision('exhaustive')}>{t.exhaustive} <Badge bg="warning" text="dark" className="ms-1" style={{ fontSize: '0.5rem' }}>PRO</Badge></Button></div></div>
                <Row className="g-3"><Col xs={6}><Button variant="primary" className="w-100 py-4 fw-bold rounded-4 d-flex flex-column align-items-center justify-content-center" onClick={() => photoInputRef.current.click()}><FaCamera size={24} className="mb-2" /><span>{t.photo}</span></Button></Col><Col xs={6}><Button variant="outline-primary" className="w-100 py-4 fw-bold rounded-4 d-flex flex-column align-items-center justify-content-center" onClick={() => videoInputRef.current.click()}><FaVideo size={24} className="mb-2" /><span>{t.video}</span></Button></Col><Col xs={6}><Button variant="outline-secondary" className="w-100 py-4 fw-bold rounded-4 d-flex flex-column align-items-center justify-content-center" onClick={() => fileInputRef.current.click()}><FaImage size={24} className="mb-2" /><span>{t.gallery}</span></Button></Col><Col xs={6}><Button variant="outline-success" className="w-100 py-4 fw-bold rounded-4 d-flex flex-column align-items-center justify-content-center" onClick={() => { setSelectedItem({ item: '', type: '', cost_cad: 0, quantity: 1, condition: 'Excellent', certainty: 'Manual', replacement_link: '' }); setShowManualModal(true); }}><FaEdit size={24} className="mb-2" /><span>{t.manual}</span></Button></Col></Row></>}
              {bgScans.filter(s => !s.failed).length > 0 && (
                <div className="mt-3 d-flex align-items-center gap-2 text-muted small">
                  <Spinner animation="border" size="sm" variant="primary" />
                  <span>{bgScans.filter(s => !s.failed).length} {t.scansProcessing}</span>
                  {bgScans.find(s => !s.failed)?.status && <span className="text-truncate" style={{ maxWidth: 200 }}>{bgScans.find(s => !s.failed).status}</span>}
                </div>
              )}
              {bgScans.filter(s => s.failed).map(scan => (
                <Alert key={scan.id} variant="warning" className="mt-3 text-start d-flex justify-content-between align-items-center mb-0">
                  <div><strong>{t.scanFailed}:</strong> {scan.failMessage || 'Unknown error'}</div>
                  <div className="ms-3 d-flex gap-2">
                    <Button variant="outline-warning" size="sm" onClick={() => retryBgScan(scan.id, scan.filePath)}><FaRedo className="me-1"/>{t.retryAi}</Button>
                    <Button variant="outline-secondary" size="sm" onClick={() => setBgScans(prev => prev.filter(s => s.id !== scan.id))}><FaTimes /></Button>
                  </div>
                </Alert>
              ))}
              {error && <Alert variant="danger" className="mt-3 text-start d-flex justify-content-between align-items-center mb-0"><div><strong>Error:</strong> {error}</div><div className="ms-3">{uploadFailed && <Button variant="outline-danger" size="sm" onClick={() => fileInputRef.current.click()}><FaRedo className="me-1"/> {t.retryUpload}</Button>}</div></Alert>}
            </Card.Body></Card></Col></Row>
      <AnalysisInbox
        items={newItems}
        onItemsChange={setNewItems}
        onSaveComplete={async () => { await fetchInventory(true); await fetchStats(); }}
        onTaskDelete={deleteAnalysisTask}
        taskId={activeTaskId}
        onTaskIdChange={setActiveTaskId}
        roomList={roomList}
        containerList={containerList}
        t={t}
        currency={currency}
        authenticatedFetch={authenticatedFetch}
      />
      <div className="d-flex align-items-center mb-3"><h5 className="mb-0 text-muted small text-uppercase fw-bold letter-spacing-1">{t.dashboard}</h5><Button variant="link" className="text-muted ms-2 p-0 border-0" onClick={() => setShowStats(prev => !prev)} title={showStats ? "Hide" : "Show"}>{showStats ? <FaEyeSlash /> : <FaEye />}</Button></div>
      {statsError && <Alert variant="warning" className="mb-3 py-2 small d-flex justify-content-between align-items-center"><span><FaExclamationTriangle className="me-2"/>Dashboard stats could not be loaded. Your inventory items are unaffected.</span><Button variant="link" size="sm" className="p-0 ms-3" onClick={fetchStats}>Retry</Button></Alert>}
      {showStats && <Row className="mb-4"><Col md={4} className="mb-3 mb-md-0"><Card className="h-100 shadow-sm border-0 bg-primary text-white"><Card.Body className="d-flex flex-column align-items-center justify-content-center py-4"><div className="small text-uppercase opacity-75 mb-1">{t.totalValue}</div><h2 className="fw-bold mb-0">${(stats?.total_value || 0).toLocaleString()}</h2><div className="small opacity-75 mt-1">{currency}</div></Card.Body></Card></Col><Col md={4} className="mb-3 mb-md-0"><Card className="h-100 shadow-sm border-0 bg-success text-white"><Card.Body className="d-flex flex-column align-items-center justify-content-center py-4"><div className="small text-uppercase opacity-75 mb-1">{t.totalItems}</div><h2 className="fw-bold mb-0">{stats?.item_count || 0}</h2><div className="small opacity-75 mt-1">{t.acrossAllCategories}</div></Card.Body></Card></Col><Col md={4}><Card className="h-100 shadow-sm border-0"><Card.Body className="py-4"><div className="small text-uppercase text-muted mb-2">{t.topCategories}</div><div className="d-flex flex-wrap gap-2">{Object.entries(stats.category_distribution || {}).sort((a, b) => b[1] - a[1]).slice(0, 5).map(([cat, count]) => (<Badge key={cat} bg="light" text="dark" className="border px-2 py-1">{cat}: {count}</Badge>))}{Object.keys(stats.category_distribution || {}).length === 0 && <span className="text-muted small">{t.noDataYet}</span>}</div></Card.Body></Card></Col></Row>}
      <div>
        <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center mb-3 border-bottom pb-2 gap-3">
          <h4 className="mb-0">{t.fullInventory} ({activeFilterCount > 0 ? `${totalFiltered}${hasMore ? '+' : ''}` : (stats?.item_count || 0)} {t.itemsInInventory})</h4>
          <div className="d-flex flex-wrap gap-2 align-items-center">
            <div className="btn-group"><Button variant={viewMode === 'table' ? 'primary' : 'outline-primary'} size="sm" onClick={() => setViewMode('table')}><FaClipboardList /></Button><Button variant={viewMode === 'gallery' ? 'primary' : 'outline-primary'} size="sm" onClick={() => setViewMode('gallery')}><FaChartPie /></Button></div>
            <div className="position-relative"><FaSearch className="position-absolute text-muted" style={{ left: '10px', top: '10px' }} /><Form.Control size="sm" type="text" placeholder={t.searchPlaceholder} className="ps-4" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} style={{ minWidth: '160px' }}/></div>
            <Button variant={sortConfig.key === 'id' ? 'primary' : 'outline-secondary'} size="sm" onClick={() => { if (sortConfig.key === 'id') { requestSort('id'); } else { const next = { key: 'id', direction: 'desc' }; setSortConfig(next); localStorage.setItem('expatory_sort', JSON.stringify(next)); } }} title={t.recentlyAdded} className="text-nowrap"><FaClock className="me-1" />{t.recentlyAdded}</Button>
            {activeFilterCount > 0 && (
              <Button variant="outline-secondary" size="sm" onClick={clearColFilters} className="text-nowrap">
                <FaTimes className="me-1" />{t.clearFilters}<Badge bg="secondary" className="ms-1">{activeFilterCount}</Badge>
              </Button>
            )}
          </div>
        </div>
        <div className="d-flex flex-wrap gap-2 mb-4"><Badge pill bg={selectedCategory === 'All' ? 'primary' : 'light'} text={selectedCategory === 'All' ? 'white' : 'dark'} className="px-3 py-2 border cursor-pointer" onClick={() => setSelectedCategory('All')} style={{ cursor: 'pointer' }}>{t.allCategoriesLabel}</Badge>{stats?.category_distribution && Object.keys(stats.category_distribution).sort().map(cat => (<Badge key={cat} pill bg={selectedCategory === cat ? 'primary' : 'light'} text={selectedCategory === cat ? 'white' : 'dark'} className="px-3 py-2 border cursor-pointer" onClick={() => setSelectedCategory(cat)} style={{ cursor: 'pointer' }}>{cat}</Badge>))}</div>
        {viewMode === "table" ? (
          <Card className="shadow-sm border-0">
            <Table responsive hover striped className="mb-0 align-middle">
              <thead>
                <tr>
                  {selectMode && <th style={{ width: '1px' }}><Form.Check type="checkbox" checked={allVisibleSelected} onChange={handleSelectAll} /></th>}
                  <th onClick={() => requestSort("item")} style={{ cursor: "pointer", userSelect: "none", whiteSpace: "nowrap" }}>{t.item}<SortIcon colKey="item" /></th>
                  <th onClick={() => requestSort("type")} style={{ cursor: "pointer", userSelect: "none", whiteSpace: "nowrap" }}>{t.type}<SortIcon colKey="type" /></th>
                  <th onClick={() => requestSort("room")} style={{ cursor: "pointer", userSelect: "none", whiteSpace: "nowrap" }}>{t.room}<SortIcon colKey="room" /></th>
                  <th onClick={() => requestSort("container")} style={{ cursor: "pointer", userSelect: "none", whiteSpace: "nowrap" }}>{t.storage}<SortIcon colKey="container" /></th>
                  <th onClick={() => requestSort("year_purchased")} style={{ cursor: "pointer", userSelect: "none", whiteSpace: "nowrap" }}>{t.yearPurchased}<SortIcon colKey="year_purchased" /></th>
                  <th onClick={() => requestSort("quantity")} style={{ cursor: "pointer", userSelect: "none", whiteSpace: "nowrap" }}>{t.qty}<SortIcon colKey="quantity" /></th>
                  <th onClick={() => requestSort("cost_cad")} style={{ cursor: "pointer", userSelect: "none", whiteSpace: "nowrap" }}>{t.cost}<SortIcon colKey="cost_cad" /></th>
                  <th onClick={() => requestSort("condition")} style={{ cursor: "pointer", userSelect: "none", whiteSpace: "nowrap" }}>{t.cond}<SortIcon colKey="condition" /></th>
                  {isEditing && <th>{t.delete}</th>}
                </tr>
                <tr className="table-secondary">
                  {selectMode && <td />}
                  <td><Form.Control size="sm" placeholder={t.filterItem} value={colFilters.item} onChange={e => setColFilters(p => ({ ...p, item: e.target.value }))} /></td>
                  <td><Form.Select size="sm" value={colFilters.type} onChange={e => setColFilters(p => ({ ...p, type: e.target.value }))}><option value="">{t.allCategories}</option>{CATEGORIES.map((cat, i) => <option key={i} value={cat}>{cat}</option>)}</Form.Select></td>
                  <td><Form.Select size="sm" value={colFilters.room} onChange={e => setColFilters(p => ({ ...p, room: e.target.value }))}><option value="">{t.allRooms}</option>{roomList.map((r, i) => <option key={i} value={r}>{r}</option>)}</Form.Select></td>
                  <td><Form.Select size="sm" value={colFilters.container} onChange={e => setColFilters(p => ({ ...p, container: e.target.value }))}><option value="">{t.allStorage}</option>{containerList.map((c, i) => <option key={i} value={c}>{c}</option>)}</Form.Select></td>
                  <td /><td /><td />
                  <td><Form.Select size="sm" value={colFilters.condition} onChange={e => setColFilters(p => ({ ...p, condition: e.target.value }))}><option value="">{t.allConditions}</option><option value="Excellent">Excellent</option><option value="Good">{t.condGood}</option><option value="Fair">{t.condFair}</option><option value="Poor">{t.condPoor}</option></Form.Select></td>
                  {isEditing && <td />}
                </tr>
              </thead>
              {sortConfig.key !== 'id' && (
                <tbody>
                  <tr>
                    <td colSpan={tableColCount} className="px-3 py-1 border-bottom bg-light text-muted small">
                      {t.sortedBy} <strong>{SORT_COL_LABELS[sortConfig.key] || sortConfig.key}</strong>{' '}
                      {sortConfig.direction === 'asc' ? '↑' : '↓'}
                    </td>
                  </tr>
                </tbody>
              )}
              <tbody className="inventory-tbody" key={`${sortConfig.key}-${sortConfig.direction}-${searchTerm}-${selectedCategory}`}>
                {sortLoading ? (
                  Array.from({ length: 8 }).map((_, i) => <SkeletonRow key={i} colCount={tableColCount} />)
                ) : inventory.length === 0 ? (
                  <tr>
                    <td colSpan={tableColCount} className="text-center py-5">
                      <FaBoxOpen size={36} className="text-muted mb-3 d-block mx-auto opacity-50" />
                      <div className="fw-semibold mb-1">{t.noItems}</div>
                      <div className="text-muted small">{t.onboardingArrow}</div>
                    </td>
                  </tr>
                ) : (isEditing ? editFilteredInventory : filteredAndSortedInventory).length === 0 ? (
                  <tr><td colSpan={tableColCount} className="text-center py-4 text-muted">{t.noMatches}</td></tr>
                ) : (isEditing ? editFilteredInventory : filteredAndSortedInventory).map((item) => {
                  const dItem = item;
                  return (
                    <tr key={dItem.id}
                      onClick={() => selectMode ? handleRowSelect(dItem.id) : (!isEditing && handleSelectItem(dItem))}
                      style={{ cursor: isEditing ? "default" : "pointer" }}
                      className={selectMode && selectedIds.has(dItem.id) ? 'table-primary' : ''}>
                      {selectMode && (
                        <td style={{ width: '1px' }} onClick={e => e.stopPropagation()}>
                          <Form.Check type="checkbox" checked={selectedIds.has(dItem.id)} onChange={() => handleRowSelect(dItem.id)} />
                        </td>
                      )}
                      <td>
                        <div className="d-flex align-items-center">
                          <div className="me-3" style={{ width: "60px", height: "60px", flexShrink: 0, borderRadius: "4px", overflow: "hidden", background: "#f8f9fa" }}>
                            {(dItem.thumbnail_file || dItem.source_file) ? (
                              <SecureImage
                                src={dItem.thumbnail_file
                                  ? `${API_BASE}/${dItem.thumbnail_file}`
                                  : `${API_BASE}/media/${dItem.source_file.split('/').pop()}`}
                                alt="thumb"
                                style={{ width: "100%", height: "100%", objectFit: "cover" }}
                              />
                            ) : (
                              <FaImage size={20} className="text-muted m-3"/>
                            )}
                          </div>
                          {isEditing ? (
                            <Form.Control type="text" value={dItem.item} onChange={(e) => handleEditChange(dItem.id, "item", e.target.value)} />
                          ) : (
                            <span className="fw-bold">{dItem.item}</span>
                          )}
                        </div>
                      </td>
                      <td>{isEditing ? <Form.Select size="sm" value={dItem.type || ''} onChange={(e) => handleEditChange(dItem.id, "type", e.target.value)}><option value="">{t.selectCategory}</option>{CATEGORIES.map((cat, i) => <option key={i} value={cat}>{cat}</option>)}</Form.Select> : <Badge bg="light" text="dark" className="border">{dItem.type}</Badge>}</td>
                      <td>{isEditing ? <Form.Select size="sm" value={dItem.room || ''} onChange={(e) => handleEditChange(dItem.id, "room", e.target.value)}><option value="">{t.selectRoom}</option>{roomList.map((r, i) => <option key={i} value={r}>{r}</option>)}</Form.Select> : <span className="text-muted small">{dItem.room}</span>}</td>
                      <td>{isEditing ? <Form.Select size="sm" value={dItem.container || ''} onChange={(e) => handleEditChange(dItem.id, "container", e.target.value)}><option value="">{t.selectStorage}</option>{containerList.map((c, i) => <option key={i} value={c}>{c}</option>)}</Form.Select> : <span className="text-muted small">{dItem.container}</span>}</td>
                      <td>{isEditing ? <Form.Control type="number" value={dItem.year_purchased} onChange={(e) => handleEditChange(dItem.id, "year_purchased", e.target.value)} /> : <span className="text-muted small">{dItem.year_purchased}</span>}</td>
                      <td>{isEditing ? <Form.Control type="number" value={dItem.quantity} onChange={(e) => handleEditChange(dItem.id, "quantity", e.target.value)} /> : dItem.quantity}</td>
                      <td>{isEditing ? <Form.Control type="number" value={dItem.cost_cad} onChange={(e) => handleEditChange(dItem.id, "cost_cad", e.target.value)} /> : <span className="text-success fw-bold">${(dItem.cost_cad || 0).toLocaleString()}</span>}</td>
                      <td>{isEditing ? (
                        <Form.Select value={dItem.condition} onChange={(e) => handleEditChange(dItem.id, "condition", e.target.value)}>
                          <option>Excellent</option>
                          <option>Good</option>
                          <option>Fair</option>
                          <option>Poor</option>
                        </Form.Select>
                      ) : (
                        <Badge bg={dItem.condition === "Excellent" ? "success" : dItem.condition === "Good" ? "info" : "warning"}>{dItem.condition}</Badge>
                      )}</td>
                      {isEditing && (
                        <td>
                          <Button variant="outline-danger" size="sm" onClick={() => handleDeleteItem(dItem.id)}>
                            <FaTrash />
                          </Button>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </Card>
        ) : (
          <Row className="g-3">
            {filteredAndSortedInventory.map((item) => (
              <Col key={item.id} xs={12} sm={6} md={4} lg={3}>
                <Card className="h-100 shadow-sm border-0 gallery-card hover-lift" onClick={() => handleSelectItem(item)} style={{ cursor: "pointer", overflow: "hidden", borderRadius: "12px" }}>
                  <SmartThumbnail item={item} />
                  <Card.Body className="p-3">
                    <div className="d-flex justify-content-between align-items-start mb-2">
                      <h6 className="fw-bold mb-0 text-truncate" style={{ maxWidth: "70%" }}>{item.item}</h6>
                      <Badge bg="success-subtle" text="success" className="border border-success-subtle">${(item.cost_cad || 0).toLocaleString()}</Badge>
                    </div>
                    <div className="d-flex justify-content-between align-items-center">
                      <span className="text-muted small"><FaBoxOpen className="me-1" /> {item.room}</span>
                      <Badge bg="light" text="dark" className="border" style={{ fontSize: "0.65rem" }}>{item.type}</Badge>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>
        )}
        <div className="d-flex justify-content-between align-items-center mt-3 px-1">
          <small className="text-muted">
            {totalFiltered}{hasMore ? '+' : ''} {t.itemsInInventory}
          </small>
          {loadingMore && <Spinner size="sm" animation="border" className="text-muted" />}
        </div>
        <div ref={sentinelRef} style={{ height: '1px' }} />
      </div>
          </>
      )}

      <BulkEditBar
        isEditing={isEditing}
        selectMode={selectMode}
        dirtyCount={dirtyItemIds.size}
        onEditList={toggleEditMode}
        onSave={saveChanges}
        onCancelEdit={toggleEditMode}
        onToggleSelect={toggleSelectMode}
        selectedCount={selectedIds.size}
        selectedIds={selectedIds}
        onApply={handleBulkApply}
        onDelete={handleBulkDelete}
        onCancelSelect={toggleSelectMode}
        roomList={roomList}
        containerList={containerList}
        categories={CATEGORIES}
        t={t}
      />

      <ItemDetailModal
        show={showDetail}
        item={selectedItem}
        onItemChange={setSelectedItem}
        onHide={() => setShowDetail(false)}
        onSaveComplete={async () => {
          const y = window.scrollY;
          setInventory(prev => prev.map(i => i.id === selectedItem.id ? { ...i, ...selectedItem } : i));
          await fetchStats();
          requestAnimationFrame(() => window.scrollTo(0, y));
        }}
        onDelete={handleDeleteItem}
        onNavigate={navigateItem}
        canNavigatePrev={filteredAndSortedInventory.findIndex(i => i.id === selectedItem?.id) > 0}
        canNavigateNext={filteredAndSortedInventory.findIndex(i => i.id === selectedItem?.id) < filteredAndSortedInventory.length - 1 || hasMore}
        t={t}
        currency={currency}
        location={location}
        authenticatedFetch={authenticatedFetch}
        roomList={roomList}
        containerList={containerList}
      />

      <SettingsModal
        show={showSettingsModal}
        onHide={() => setShowSettingsModal(false)}
        t={t}
        passwordForm={passwordForm} setPasswordForm={setPasswordForm}
        handleChangePassword={handleChangePassword} passwordLoading={passwordLoading}
        emailForm={emailForm} setEmailForm={setEmailForm}
        handleEmailChange={handleEmailChange} emailLoading={emailLoading}
        deleteForm={deleteForm} setDeleteForm={setDeleteForm}
        handleDeleteAccount={handleDeleteAccount} deleteLoading={deleteLoading}
        onExportExcel={() => { setShowSettingsModal(false); setShowExcelModal(true); }}
        onExportPdf={() => { setShowSettingsModal(false); setShowPdfModal(true); }}
        roomList={roomList} containerList={containerList} saveSettings={saveSettings}
        language={language} setLanguage={setLanguage}
        currency={currency} setCurrency={setCurrency}
        location={location} setLocation={setLocation}
      />

      <Modal show={showPdfModal} onHide={() => !pdfExporting && setShowPdfModal(false)} centered><Modal.Header closeButton><Modal.Title><FaFilePdf className="text-danger"/> {t.exportPdf}</Modal.Title></Modal.Header><Modal.Body><Form><Form.Check type="switch" label={t.includeImages} checked={pdfOptions.includeImages} onChange={(e) => setPdfOptions({...pdfOptions, includeImages: e.target.checked})} /><hr/><Form.Label className="fw-semibold">{t.filterByRoom}</Form.Label><div className="mt-1"><Form.Check type="checkbox" label={t.allRooms} checked={pdfOptions.rooms.length === 0} onChange={() => setPdfOptions({...pdfOptions, rooms: []})} />{roomList.map(r => <Form.Check key={r} type="checkbox" label={r} checked={pdfOptions.rooms.includes(r)} onChange={(e) => { const next = e.target.checked ? [...pdfOptions.rooms, r] : pdfOptions.rooms.filter(x => x !== r); setPdfOptions({...pdfOptions, rooms: next}); }} />)}</div></Form></Modal.Body><Modal.Footer><Button variant="secondary" onClick={() => setShowPdfModal(false)} disabled={pdfExporting}>{t.cancel}</Button><Button variant="primary" onClick={handleExportPDF} disabled={pdfExporting}>{pdfExporting ? <><Spinner animation="border" size="sm" className="me-1"/>{t.exporting}</> : t.generateReport}</Button></Modal.Footer></Modal>
      <Modal show={showExcelModal} onHide={() => !excelExporting && setShowExcelModal(false)} centered><Modal.Header closeButton><Modal.Title><FaFileExcel className="text-success"/> {t.export}</Modal.Title></Modal.Header><Modal.Body><Form><Form.Check type="switch" label={t.includeImages} checked={excelOptions.includeImages} onChange={(e) => setExcelOptions({...excelOptions, includeImages: e.target.checked})} />{excelOptions.includeImages && <Alert variant="warning" className="mt-2 mb-0 py-2 small"><FaExclamationTriangle className="me-1"/>{t.exportImageWarning}</Alert>}<hr/><Form.Label>{t.filterByRoom}</Form.Label><Form.Select value={excelOptions.room} onChange={(e) => setExcelOptions({...excelOptions, room: e.target.value})}><option value="">{t.allRooms}</option>{roomList.map(r => <option key={r} value={r}>{r}</option>)}</Form.Select></Form></Modal.Body><Modal.Footer><Button variant="secondary" onClick={() => setShowExcelModal(false)} disabled={excelExporting}>{t.cancel}</Button><Button variant="success" onClick={handleExportExcel} disabled={excelExporting}>{excelExporting ? <><Spinner animation="border" size="sm" className="me-1"/>{t.exporting}</> : t.export}</Button></Modal.Footer></Modal>
      <Modal show={showManualModal} onHide={() => setShowManualModal(false)} centered><Modal.Header closeButton><Modal.Title>{t.manualEntryTitle}</Modal.Title></Modal.Header><Modal.Body>{selectedItem && (<Form><Form.Group className="mb-2"><Form.Label>{t.item}</Form.Label><Form.Control type="text" value={selectedItem.item} onChange={(e) => setSelectedItem(prev => ({ ...prev, item: e.target.value }))} /></Form.Group><Row><Col><Form.Label>{t.cost}</Form.Label><Form.Control type="number" value={selectedItem.cost_cad ?? ''} onChange={(e) => setSelectedItem(prev => ({ ...prev, cost_cad: e.target.value }))} /></Col><Col><Form.Label>{t.qty}</Form.Label><Form.Control type="number" value={selectedItem.quantity ?? ''} onChange={(e) => setSelectedItem(prev => ({ ...prev, quantity: e.target.value }))} /></Col></Row></Form>)}</Modal.Body><Modal.Footer><Button variant="secondary" onClick={() => setShowManualModal(false)}>{t.cancel}</Button><Button variant="success" onClick={saveManualEntry}>{t.addToInventory}</Button></Modal.Footer></Modal>
      <Modal show={showEraseConfirm} onHide={() => setShowEraseConfirm(false)} centered><Modal.Header closeButton className="bg-danger text-white"><Modal.Title>{t.dangerZone}</Modal.Title></Modal.Header><Modal.Body><p className="fw-bold">{t.confirmDeleteAllTitle}</p><p>{t.confirmDeleteAllBody}</p></Modal.Body><Modal.Footer><Button variant="secondary" onClick={() => setShowEraseConfirm(false)}>{t.cancel}</Button><Button variant="danger" onClick={handleEraseAll}>{t.deleteAllConfirm}</Button></Modal.Footer></Modal>
      <Modal show={showUAPModal} onHide={() => setShowUAPModal(false)} size="lg" centered scrollable><Modal.Header closeButton><Modal.Title>{t.uapTitle}</Modal.Title></Modal.Header><Modal.Body><Alert variant="info" className="small">{t.uapSummary}</Alert>{[1,2,3,4,5,6,7,8,9,10,11].map(n => (<div className="mb-3" key={n}><h6>{t[`uapSection${n}Title`]}</h6><p className="small text-muted">{t[`uapSection${n}Content`]}</p></div>))}<div className="border-top pt-3 mt-3 small text-muted">Last Updated: March 10, 2026</div></Modal.Body><Modal.Footer><Button variant="primary" onClick={() => setShowUAPModal(false)}>{t.close}</Button></Modal.Footer></Modal>

      <Modal show={showAbout} onHide={() => setShowAbout(false)} centered>
        <Modal.Header closeButton><Modal.Title><FaInfoCircle className="me-2"/>{t.about}</Modal.Title></Modal.Header>
        <Modal.Body><p>{t.aboutContent}</p></Modal.Body>
        <Modal.Footer><Button variant="primary" onClick={() => setShowAbout(false)}>{t.close}</Button></Modal.Footer>
      </Modal>

      <Modal show={showTutorial} onHide={() => setShowTutorial(false)} size="lg" centered>
        <Modal.Header closeButton><Modal.Title><FaQuestionCircle className="me-2"/>{t.tutorial}</Modal.Title></Modal.Header>
        <Modal.Body>
          {[1,2,3,4,5].map(n => (
            <div className="mb-3" key={n}>
              <h6 className="fw-bold">{t[`tutorialStep${n}Title`]}</h6>
              <p className="text-muted small mb-0">{t[`tutorialStep${n}Content`]}</p>
            </div>
          ))}
        </Modal.Body>
        <Modal.Footer><Button variant="primary" onClick={() => setShowTutorial(false)}>{t.close}</Button></Modal.Footer>
      </Modal>

      <Modal show={showOnboarding} onHide={dismissOnboarding} centered size="md">
        <Modal.Header closeButton className="border-0 pb-0">
          <Modal.Title className="fw-bold fs-5">
            <FaBoxOpen className="me-2 text-primary" />{t.onboardingTitle}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="pt-2 pb-1">
          <p className="text-muted small mb-4">{t.onboardingSubtitle}</p>
          {[1, 2, 3].map(n => (
            <div key={n} className="d-flex mb-3">
              <div className="me-3 mt-1" style={{ width: '28px', height: '28px', borderRadius: '50%', background: 'var(--bs-primary)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.75rem', fontWeight: 700, flexShrink: 0 }}>{n}</div>
              <div>
                <div className="fw-semibold" style={{ fontSize: '0.95rem' }}>{t[`onboardingStep${n}Title`]}</div>
                <div className="text-muted small">{t[`onboardingStep${n}Body`]}</div>
              </div>
            </div>
          ))}
        </Modal.Body>
        <Modal.Footer className="border-0 flex-column align-items-stretch gap-2 pt-0">
          <Button variant="primary" className="w-100 fw-semibold py-2" onClick={dismissOnboarding}>
            {t.onboardingCta}
          </Button>
          <Button variant="link" size="sm" className="text-muted text-decoration-none" onClick={dismissOnboarding}>
            {t.onboardingSkip}
          </Button>
        </Modal.Footer>
      </Modal>
      </Container>

      {showImportWizard && (
        <ImportWizard
          authenticatedFetch={authenticatedFetch}
          t={t}
          onClose={() => setShowImportWizard(false)}
          onComplete={async () => {
            setShowImportWizard(false);
            await fetchInventory(true);
            await fetchStats();
          }}
        />
      )}
    </div>
  );
}

export default App;
